import React, { useState, useEffect, useRef, useCallback } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { v4 as uuidv4 } from "uuid";
import { useAPI } from "../contexts/APIContext";
import { useAuth } from "../contexts/AuthContext";
import { withReferral } from "../utils/withReferral";
import LoadingScreen from "../components/LoadingScreen";
import ShareModal from "../components/ShareModal";
import ShareVideoModal from "../components/ShareVideoModal";
import FeedbackModal from "../components/FeedbackModal";
import PaywallModal from "../components/PaywallModal";
import { useEntitlementGate } from "../contexts/EntitlementGateContext";
import { showToast } from "../components/Toast";
import logger from "../utils/logger";
import AudiobookFlipBook from "../components/audiobook/AudiobookFlipBook";
import AudiobookSinglePage from "../components/audiobook/AudiobookSinglePage";
import AudiobookProgressBar from "../components/audiobook/AudiobookProgressBar";
import RecommendedStorybooks from "../components/storybooks/RecommendedStorybooks";
import LikeButton from "../components/common/LikeButton";
import ConfirmationModal from "../components/ConfirmationModal";
import VoiceSessionPanel from "../components/audiobook/VoiceSessionPanel";
import useVoiceStorybookSession from "../hooks/useVoiceStorybookSession";
import videoDownloadManager from "../utils/videoDownloadManager";
import { clampStorybookResumePage } from "../utils/progressStats";
import { updateStorybookProgress } from "../utils/progressTracking";
import { useAnalytics, usePageTracking } from "../hooks/useAnalytics";
import { setShareMeta } from '../utils/shareMeta';

// Per-audiobook feedback dismissal tracking (in-memory + sessionStorage)
const getFeedbackDismissed = (audiobookId) => {
  if (!audiobookId) return false;
  // Check sessionStorage first
  const stored = sessionStorage.getItem(`feedback_dismissed_${audiobookId}`);
  return stored === "true";
};

const setFeedbackDismissed = (audiobookId, dismissed = true) => {
  if (!audiobookId) return;
  if (dismissed) {
    sessionStorage.setItem(`feedback_dismissed_${audiobookId}`, "true");
  } else {
    sessionStorage.removeItem(`feedback_dismissed_${audiobookId}`);
  }
};

const AudiobookPlayer = () => {
  usePageTracking('Audiobook Player');
  const { trackAudiobookPlayed, trackAudiobookPageTurned, trackAudiobookCompleted, trackContentDownloaded, trackVisibilityToggled } = useAnalytics();
  const { audiobookId } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { apiClient } = useAPI();
  const { user, isAuthenticated, subscriptionStatus, costs, updateUser, refreshUserData } = useAuth();
  const { requireEntitlement } = useEntitlementGate();
  const [audiobook, setAudiobook] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentPageIndex, setCurrentPageIndex] = useState(0);
  // Voice-live text reveal runs once per page: indexes the reader has already
  // sat on get added here when they flip away, so flipping back renders the
  // text instantly instead of replaying the word-by-word animation.
  const animatedTextPageIndexesRef = useRef(new Set());
  const lastViewedPageIndexRef = useRef(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [volume, setVolume] = useState(1);
  const [playbackRate, setPlaybackRate] = useState(1);
  const [isContinuousPlay, setIsContinuousPlay] = useState(false);
  const [isSpreadLayout, setIsSpreadLayout] = useState(false);
  const [isShareModalOpen, setIsShareModalOpen] = useState(false);
  const [isShareVideoModalOpen, setIsShareVideoModalOpen] = useState(false);
  // TEMP: PDF export test control — remove or move into Share/More later.
  const [isDownloadingPdf, setIsDownloadingPdf] = useState(false);

  const handleDownloadPdf = useCallback(async () => {
    if (!audiobookId || isDownloadingPdf) return;

    // Same gate as Download Video: guests and unentitled users get the
    // wall/paywall popup instead of a raw API auth error.
    if (!requireEntitlement({ trigger: "download", contentType: "storybook_pdf" })) {
      return;
    }
    if (!isAuthenticated || costs?.downloadAbility !== true) {
      setIsPaywallModalOpen(true);
      return;
    }

    // Prefer context client; fall back to window client after Fast Refresh rebuilds it.
    const liveClient =
      (apiClient && typeof apiClient.downloadAudiobookPdf === "function" && apiClient) ||
      (typeof window !== "undefined" &&
        window.pixleyApiClient &&
        typeof window.pixleyApiClient.downloadAudiobookPdf === "function" &&
        window.pixleyApiClient) ||
      null;

    if (!liveClient) {
      showToast("PDF download is unavailable. Please refresh the page.", "error");
      return;
    }

    setIsDownloadingPdf(true);
    try {
      const shareToken = searchParams.get("token");
      const result = await liveClient.downloadAudiobookPdf(
        audiobookId,
        audiobook?.title || "storybook",
        shareToken,
      );
      if (result?.success) {
        showToast("PDF downloaded", "success");
      } else {
        showToast(result?.error || "Failed to download PDF", "error");
      }
    } catch (err) {
      showToast(err?.message || "Failed to download PDF", "error");
    } finally {
      setIsDownloadingPdf(false);
    }
  }, [apiClient, audiobookId, audiobook?.title, isDownloadingPdf, searchParams, requireEntitlement, isAuthenticated, costs?.downloadAbility]);

  // Open the share modal, minting a share_token first if this storybook isn't
  // shared yet (owner only) so the copied link carries ?token= and resolves.
  const handleOpenShare = async () => {
    const isOwner = audiobook && user?.id && audiobook.user_id === user.id;
    if (isOwner && !audiobook.share_token) {
      try {
        const resp = await apiClient.setAudiobookSharing(audiobookId, true);
        if (resp?.status === "success" && resp?.share_token) {
          setAudiobook((prev) => ({ ...prev, share_token: resp.share_token }));
        }
      } catch (e) {
        logger.error("Failed to enable storybook sharing:", e);
      }
    }
    setIsShareModalOpen(true);
  };
  const [isPaywallModalOpen, setIsPaywallModalOpen] = useState(false);
  const [isUpdatingVisibility, setIsUpdatingVisibility] = useState(false);
  const [showFeedbackModal, setShowFeedbackModal] = useState(false);
  const [showFeedbackNotification, setShowFeedbackNotification] =
    useState(false);
  const [showLanguageDropdown, setShowLanguageDropdown] = useState(false);
  const [selectedLanguage, setSelectedLanguage] = useState("en");
  const [translatedPages, setTranslatedPages] = useState(null);
  const [isTranslating, setIsTranslating] = useState(false);
  const [isGeneratingVideo, setIsGeneratingVideo] = useState(false);
  const [showVideoProgressModal, setShowVideoProgressModal] = useState(false);
  const [liked, setLiked] = useState(false);
  const [likeLoading, setLikeLoading] = useState(false);
  const [showMoreMenu, setShowMoreMenu] = useState(false);
  const [isDesktopView, setIsDesktopView] = useState(
    typeof window !== "undefined" ? window.innerWidth >= 1200 : true
  );
  const audioRef = useRef(null);
  const languageDropdownMobileRef = useRef(null);
  const languageDropdownDesktopRef = useRef(null);
  const moreMenuRef = useRef(null);
  const hasLoadedRef = useRef(false); // Guard to prevent duplicate calls in StrictMode
  const hasIncrementedViewRef = useRef(false); // Guard to ensure view increment happens only once per page load
  const feedbackNotificationTimeoutRef = useRef(null);
  // session_id for POST /audiobooks/events: created once on mount, reused for entire listening session (same ref for all play/pause/seek/completed events).
  const audiobookSessionId = useRef(null);
  const hasTrackedPlayRef = useRef(false); // Guard to prevent duplicate PLAYAUDIOBOOK tag
  const hasTrackedCompleteRef = useRef(false); // Guard to prevent duplicate COMPLETEAUDIOBOOK tag
  const hasTrackedAudiobookCompletedRef = useRef(false); // Guard: audiobook_completed sent only once per session
  const seekFromTimeRef = useRef(0); // from_sec for seek/skip events (set on seeking)
  const pendingSkipRef = useRef(null); // { type: 'skip_forward'|'skip_backward', from: number } when skip button used
  const lastCurrentTimeBeforeZeroRef = useRef(0); // for replay: time before user set currentTime to 0 (e.g. Stop)
  const displayPagesRef = useRef(null); // latest displayPages, for stable callbacks
  const autoPlayNextRef = useRef(false); // when true, the current page's audio auto-plays once its src loads
  const lastStorybookProgressRef = useRef({ page: null, status: null });
  const resumePageParam = Number(searchParams.get("resumePage") || "0");
  const accessToken = searchParams.get("token");
  const resumePageIndex =
    Number.isFinite(resumePageParam) && resumePageParam > 0
      ? Math.floor(resumePageParam)
      : 0;

  // ─────────────────── AI Voice Storybook (Gemini Live) ───────────────────
  // Voice storybooks reuse this exact reader. The only difference: while the
  // Live conversation hasn't finished, we bridge a WebSocket for mic/audio/
  // transcripts and let pages stream in live. Once the backend marks the
  // story "ready"/"complete", this collapses back into a normal audiobook —
  // no more WS, no more special-casing.
  const isVoiceStorybook = audiobook?.creation_method === "ai_voice";
  const isVoiceLive =
    isVoiceStorybook &&
    audiobook?.voice_phase !== "complete" &&
    audiobook?.status?.toLowerCase() !== "ready" &&
    audiobook?.status?.toLowerCase() !== "completed";
  const [showVoiceUpgradeDialog, setShowVoiceUpgradeDialog] = useState(false);
  const [isPurchasingContinue, setIsPurchasingContinue] = useState(false);
  const hasShownInitialUpgradePromptRef = useRef(false);

  // page_pending arrives the instant Gemini commits the page TEXT — flip to a
  // text-only page immediately; page_created later swaps in the illustration
  // and narration audio (merged by page_number below).
  const handleVoicePagePending = useCallback((message) => {
    const pageNumber = message?.page_number;
    const text = message?.text;
    if (!pageNumber || !text) return;
    setAudiobook((prev) => {
      if (!prev) return prev;
      const pages = Array.isArray(prev.pages) ? [...prev.pages] : [];
      if (pages.some((p) => p.page_number === pageNumber)) return prev;
      pages.push({ page_number: pageNumber, text, image_url: null, audio_url: null, pending: true });
      pages.sort((a, b) => (a.page_number || 0) - (b.page_number || 0));
      // +1 to account for the synthetic cover page prepended in displayPages.
      setCurrentPageIndex(pages.length);
      return { ...prev, pages };
    });
  }, []);

  const applyVoicePage = useCallback((message, page) => {
    setAudiobook((prev) => {
      if (!prev) return prev;
      const pages = Array.isArray(prev.pages) ? [...prev.pages] : [];
      let isNewPage = true;
      if (page) {
        const existingIndex = pages.findIndex((p) => p.page_number === page.page_number);
        if (existingIndex >= 0) {
          pages[existingIndex] = page;
          isNewPage = false;
        } else {
          pages.push(page);
        }
        pages.sort((a, b) => (a.page_number || 0) - (b.page_number || 0));
      }
      if (isNewPage) {
        // +1 to account for the synthetic cover page prepended in displayPages.
        setCurrentPageIndex(pages.length);
      }
      return {
        ...prev,
        pages,
        current_page: message?.current_page ?? prev.current_page,
        voice_page_limit: message?.voice_page_limit ?? prev.voice_page_limit,
        needs_upgrade: message?.needs_upgrade ?? prev.needs_upgrade,
      };
    });
  }, []);

  const handleVoicePageCreated = useCallback((message) => {
    const page = message?.page;
    // Preload the illustration BEFORE swapping it into the page — merging an
    // un-cached image_url blanked the just-shown text page while the browser
    // fetched the image. The text-only pending page stays visible until the
    // illustration can paint in one frame.
    // Returns a Promise so the voice hook can send page_ready after the
    // visible page has switched (avoids Gemini's 2.5s page_created timeout).
    return new Promise((resolve) => {
      const apply = () => {
        applyVoicePage(message, page);
        requestAnimationFrame(() => resolve());
      };
      if (page?.image_url) {
        // Freshly-uploaded storage URLs can 404 for a beat (propagation race);
        // an errored first fetch made the page <img> fall back to the panda
        // placeholder until a later re-render retried. Retry the preload a few
        // times before applying so the page swaps in already-loadable.
        let attempts = 0;
        const tryLoad = () => {
          const img = new Image();
          img.onload = apply;
          img.onerror = () => {
            attempts += 1;
            if (attempts < 3) setTimeout(tryLoad, attempts * 800);
            else apply();
          };
          img.src = page.image_url;
        };
        tryLoad();
      } else {
        apply();
      }
    });
  }, [applyVoicePage]);

  const handleVoiceNeedsUpgrade = useCallback(() => {
    // Soft signal — keep listening; popup waits for upgrade_ready.
    setAudiobook((prev) => (prev ? { ...prev, needs_upgrade: true } : prev));
  }, []);

  const handleVoiceUpgradeReady = useCallback(() => {
    setShowVoiceUpgradeDialog(true);
    setIsPurchasingContinue(false);
    setAudiobook((prev) =>
      prev ? { ...prev, needs_upgrade: true, voice_phase: "paused_upgrade" } : prev,
    );
  }, []);

  const refreshAudiobookFromBackend = useCallback(async () => {
    if (!apiClient || !audiobookId) return;
    try {
      const response = await apiClient.getAudiobook(audiobookId, accessToken, true);
      if (response?.status && response?.data) {
        setAudiobook(response.data);
        if (response.data?.title) {
          setShareMeta({
            title: `Read “${response.data.title}” — made with Pixley`,
            description: 'A storybook made with Pixley. Start your trial and create your own.',
            image: response.data.cover_image_url || response.data.thumbnail_url,
          });
        }
      }
    } catch (err) {
      logger.error("Error refreshing voice storybook after completion:", err);
    }
  }, [apiClient, audiobookId, accessToken]);

  const handleVoiceLimitUpgraded = useCallback((message) => {
    setShowVoiceUpgradeDialog(false);
    setIsPurchasingContinue(false);
    setAudiobook((prev) =>
      prev
        ? {
            ...prev,
            needs_upgrade: false,
            continue_purchased: true,
            voice_page_limit: message?.voice_page_limit ?? prev.voice_page_limit,
            voice_phase: message?.voice_phase ?? "live",
          }
        : prev,
    );
  }, []);

  const handleVoiceSessionReplaced = useCallback((message) => {
    showToast(message?.message || "This story is being read in another tab.", "info");
  }, []);

  const handleVoiceError = useCallback((message) => {
    if (message?.message) {
      showToast(message.message, "error");
    }
  }, []);

  // Celebratory full-screen ending. Ordering: `ready` (cover rendered) and
  // `session_complete` (goodbye finished playing) can arrive in either order;
  // the celebration waits for the goodbye AND for local audio to drain, so
  // the narrator finishes speaking before the screen takes over.
  const [voiceEndingInfo, setVoiceEndingInfo] = useState(null);
  const [voiceSessionDone, setVoiceSessionDone] = useState(false);
  const [showVoiceEnding, setShowVoiceEnding] = useState(false);
  const handleVoiceReady = useCallback((message) => {
    setVoiceEndingInfo({
      title: message?.title,
      thumbnail_url: message?.thumbnail_url,
    });
    refreshAudiobookFromBackend();
  }, [refreshAudiobookFromBackend]);

  const handleVoiceSessionComplete = useCallback(async (message) => {
    setVoiceSessionDone(true);
    // Reconnect-to-already-complete: backend sends session_complete and closes.
    // Refresh so isVoiceLive becomes false and we show the finished book —
    // never keep trying to resume the conversation.
    if (!apiClient || !audiobookId) {
      refreshAudiobookFromBackend();
      return;
    }
    try {
      const response = await apiClient.getAudiobook(audiobookId, accessToken, true);
      if (response?.status && response?.data) {
        const data = response.data;
        setAudiobook(data);
        setVoiceEndingInfo((prev) => ({
          title: prev?.title || message?.title || data.title,
          thumbnail_url:
            prev?.thumbnail_url ||
            message?.thumbnail_url ||
            data.thumbnail_url ||
            data.cover_url ||
            data.cover_image_url,
        }));
      }
    } catch (err) {
      logger.error("Error refreshing voice storybook after session_complete:", err);
    }
  }, [apiClient, audiobookId, accessToken, refreshAudiobookFromBackend]);

  const voiceSession = useVoiceStorybookSession({
    enabled: isVoiceLive,
    sessionId: audiobook?.voice_session_id || null,
    apiClient,
    onPagePending: handleVoicePagePending,
    onPageCreated: handleVoicePageCreated,
    onNeedsUpgrade: handleVoiceNeedsUpgrade,
    onUpgradeReady: handleVoiceUpgradeReady,
    onReady: handleVoiceReady,
    onSessionComplete: handleVoiceSessionComplete,
    onLimitUpgraded: handleVoiceLimitUpgraded,
    onSessionReplaced: handleVoiceSessionReplaced,
    onError: handleVoiceError,
  });

  // If the backend auto-finalized while we were disconnected, stop treating
  // this as a live conversation and show the completed book.
  useEffect(() => {
    if (!isVoiceLive || !apiClient || !audiobookId) return undefined;
    const poll = async () => {
      try {
        const response = await apiClient.getAudiobook(audiobookId, accessToken, true);
        const data = response?.data;
        if (!response?.status || !data) return;
        const s = String(data.status || "").toLowerCase();
        if (s === "ready" || s === "completed" || data.voice_phase === "complete") {
          setAudiobook(data);
        }
      } catch (err) {
        logger.error("Error polling voice storybook finalization:", err);
      }
    };
    const id = setInterval(poll, 4000);
    return () => clearInterval(id);
  }, [isVoiceLive, apiClient, audiobookId, accessToken]);

  // Show the celebration when the session is done and (if still speaking)
  // local conversation audio has drained. Title/cover come from the backend
  // `ready` payload and/or a refreshed audiobook — never require the user to
  // type a title. Fallback: if cover preload stalls, show after 20s.
  useEffect(() => {
    if (!voiceSessionDone || voiceSession.isAssistantSpeaking || showVoiceEnding) return;
    let cancelled = false;
    const show = () => {
      if (!cancelled) setShowVoiceEnding(true);
    };
    const coverUrl = voiceEndingInfo?.thumbnail_url || audiobook?.thumbnail_url || audiobook?.cover_url;
    let fallbackTimer = null;
    if (coverUrl) {
      const img = new Image();
      img.onload = show;
      img.onerror = show;
      img.src = coverUrl;
    } else if (voiceEndingInfo?.title || audiobook?.title || audiobook?.status?.toLowerCase() === "ready") {
      // Backend already finalized (title may arrive a beat later) — don't wait forever.
      fallbackTimer = setTimeout(show, 1500);
    } else {
      fallbackTimer = setTimeout(show, 20000);
    }
    return () => {
      cancelled = true;
      if (fallbackTimer) clearTimeout(fallbackTimer);
    };
  }, [voiceSessionDone, voiceSession.isAssistantSpeaking, voiceEndingInfo, showVoiceEnding, audiobook?.thumbnail_url, audiobook?.cover_url, audiobook?.title, audiobook?.status]);

  // Reconnect while already paused for upgrade — show Continue immediately.
  // Do not wait for another websocket event.
  useEffect(() => {
    if (
      isVoiceStorybook &&
      audiobook?.voice_phase === "paused_upgrade" &&
      !audiobook?.continue_purchased &&
      !hasShownInitialUpgradePromptRef.current
    ) {
      hasShownInitialUpgradePromptRef.current = true;
      setShowVoiceUpgradeDialog(true);
    }
  }, [isVoiceStorybook, audiobook?.voice_phase, audiobook?.continue_purchased]);

  const handleContinueVoiceStorybook = useCallback(async () => {
    if (!apiClient || !audiobook?.id || isPurchasingContinue) return;
    setIsPurchasingContinue(true);
    try {
      const response = await apiClient.continueVoiceStorybook({
        sessionId: audiobook.voice_session_id,
        audiobookId: audiobook.id,
      });

      if (response?.status === "success") {
        await refreshUserData?.();
        setAudiobook((prev) =>
          prev
            ? {
                ...prev,
                voice_page_limit: response.voice_page_limit ?? prev.voice_page_limit,
                needs_upgrade: response.needs_upgrade ?? false,
                continue_purchased: response.continue_purchased ?? true,
                voice_phase: response.voice_phase ?? prev.voice_phase,
              }
            : prev,
        );
        setShowVoiceUpgradeDialog(false);
        // Backend resumes via websocket (limit_upgraded) — do not manually reconnect.
      } else {
        showToast(response?.error || response?.detail || "Not enough stars to continue this story.", "error");
      }
    } catch (err) {
      logger.error("Error purchasing Voice Storybook continue:", err);
      showToast("Something went wrong. Please try again.", "error");
    } finally {
      setIsPurchasingContinue(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [apiClient, audiobook?.id, audiobook?.voice_session_id, isPurchasingContinue, refreshUserData]);

  // Language options with flag emojis
  // Close the desktop "more actions" menu on outside click
  useEffect(() => {
    if (!showMoreMenu) return;
    const onDown = (e) => {
      if (moreMenuRef.current && !moreMenuRef.current.contains(e.target)) setShowMoreMenu(false);
    };
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, [showMoreMenu]);

  const languageOptions = [
    { code: "en", name: "English", flag: "🇺🇸" },
    { code: "es", name: "Spanish", flag: "🇪🇸" },
    { code: "fr", name: "French", flag: "🇫🇷" },
    { code: "de", name: "German", flag: "🇩🇪" },
    { code: "it", name: "Italian", flag: "🇮🇹" },
    { code: "pt", name: "Portuguese", flag: "🇧🇷" },
    { code: "zh", name: "Chinese", flag: "🇨🇳" },
    { code: "ja", name: "Japanese", flag: "🇯🇵" },
    { code: "ko", name: "Korean", flag: "🇰🇷" },
    { code: "hi", name: "Hindi", flag: "🇮🇳" },
  ];

  const getCurrentLanguage = () => {
    return (
      languageOptions.find((lang) => lang.code === selectedLanguage) ||
      languageOptions[0]
    );
  };

  // Translate audiobook pages
  const translateAudiobook = async (targetLanguage) => {
    if (!audiobook || !audiobook.pages || targetLanguage === "en") {
      setTranslatedPages(null);
      return;
    }

    // Members-only gate — translations are GPT-generated per language
    if (!requireEntitlement({ trigger: "translate", contentType: "storybook_translation" })) return;

    setIsTranslating(true);

    try {
      // Call backend translation endpoint
      const response = await apiClient.translateAudiobook(
        audiobookId,
        targetLanguage,
      );

      if (response.status && response.data) {
        setTranslatedPages(response.data.pages);
        showToast(`Translated to ${getCurrentLanguage().name}`, "success");
      } else {
        showToast(response.error || "Translation failed", "error");
        setSelectedLanguage("en"); // Revert to English
      }
    } catch (error) {
      logger.error("Translation error:", error);
      showToast("Translation failed. Please try again.", "error");
      setSelectedLanguage("en"); // Revert to English
    } finally {
      setIsTranslating(false);
    }
  };

  // Fetch audiobook data
  useEffect(() => {
    const handler = () => setIsDesktopView(window.innerWidth >= 1200);
    window.addEventListener("resize", handler);
    return () => window.removeEventListener("resize", handler);
  }, []);

  useEffect(() => {
    // Guard: Prevent duplicate calls in React.StrictMode (development mode)
    if (hasLoadedRef.current) {
      console.log(
        "🛡️ GET audiobook API call blocked - already loaded (StrictMode guard)",
      );
      return;
    }

    const loadAudiobook = async () => {
      if (!audiobookId || !apiClient) return;

      // Mark as loading to prevent duplicate calls
      hasLoadedRef.current = true;
      console.log("GET audiobook API called");

      try {
        setLoading(true);
        setError(null);

        const response = await apiClient.getAudiobook(audiobookId, accessToken, true);

        if (response.status && response.data) {
          console.log(
            "🔍 AudiobookPlayer - Full audiobook data:",
            response.data,
          );
          console.log(
            "🔍 AudiobookPlayer - thumbnail_url:",
            response.data?.thumbnail_url,
          );
          const fetchedAudiobook = response.data;
          // The server is the access authority: a 200 means viewable (books
          // use the anyone-with-the-link model, like private stories). No
          // client-side re-check — share_token is legacy and no longer sent.

          setAudiobook(fetchedAudiobook);
          if (fetchedAudiobook?.title) {
            setShareMeta({
              title: `Read “${fetchedAudiobook.title}” — made with Pixley`,
              description: 'A storybook made with Pixley. Start your trial and create your own.',
              image: fetchedAudiobook.cover_image_url || fetchedAudiobook.thumbnail_url,
            });
          }
          // Set language from audiobook data
          if (response.data.language) {
            setSelectedLanguage(response.data.language);
          }
          // Resume from saved page index when present.
          // +1 because this player prepends a cover page at index 0.
          const totalStoryPages = Array.isArray(response.data?.pages)
            ? response.data.pages.length
            : 0;
          const clampedStoryPageIndex = clampStorybookResumePage(
            resumePageIndex,
            totalStoryPages,
          );
          // AI Voice Storybooks open directly to the spread (not the closed
          // cover) so the "let's create your story together" placeholder —
          // and the mic control — are immediately visible, matching how the
          // book looks the moment real pages start streaming in.
          const opensDirectlyToSpread =
            fetchedAudiobook?.creation_method === "ai_voice" && totalStoryPages === 0;
          const resolvedPageIndex =
            clampedStoryPageIndex > 0 ? clampedStoryPageIndex + 1 : opensDirectlyToSpread ? 1 : 0;
          setCurrentPageIndex(resolvedPageIndex);

          // Add PLAYAUDIOBOOK tag when audiobook loads successfully (once per page load)
          if (user?.id && apiClient && !hasTrackedPlayRef.current) {
            hasTrackedPlayRef.current = true;
            const timestamp = new Date().toISOString();
            const tag = `PLAYAUDIOBOOK_${audiobookId}_${timestamp}`;
            apiClient
              .addUserDataTag(user.id, tag)
              .then((result) => {
                if (result.status) {
                  logger.log(
                    `✅ Added play audiobook tag ${tag} for user ${user.id}`,
                  );
                } else {
                  logger.warn(
                    `⚠️ Failed to add play audiobook tag ${tag}:`,
                    result.error,
                  );
                }
              })
              .catch((error) => {
                logger.error(
                  `❌ Error adding play audiobook tag ${tag}:`,
                  error,
                );
              });
          }
        } else {
          setError(response.error || "Storybook not found");
          // Reset guard on error so user can retry
          hasLoadedRef.current = false;
        }
      } catch (err) {
        logger.error("Error loading audiobook:", err);
        setError("Failed to load storybook");
        // Reset guard on error so user can retry
        hasLoadedRef.current = false;
      } finally {
        setLoading(false);
      }
    };

    loadAudiobook();
  }, [audiobookId, apiClient, resumePageIndex, accessToken, isAuthenticated, user?.id, navigate]);

  // Initialize audiobook session ID once per page/session — reused for all event payloads (audiobook_id comes from useParams() above).
  useEffect(() => {
    if (!audiobookSessionId.current) {
      audiobookSessionId.current = uuidv4();
    }
  }, []);

  // Click outside handler for language dropdown
  useEffect(() => {
    const handleClickOutside = (event) => {
      const isOutsideMobile =
        languageDropdownMobileRef.current &&
        !languageDropdownMobileRef.current.contains(event.target);
      const isOutsideDesktop =
        languageDropdownDesktopRef.current &&
        !languageDropdownDesktopRef.current.contains(event.target);

      if (isOutsideMobile && isOutsideDesktop) {
        setShowLanguageDropdown(false);
      }
    };

    if (showLanguageDropdown) {
      document.addEventListener("mousedown", handleClickOutside);
      return () =>
        document.removeEventListener("mousedown", handleClickOutside);
    }
  }, [showLanguageDropdown]);

  // Reset guards when audiobookId changes (user navigates to different audiobook)
  useEffect(() => {
    hasLoadedRef.current = false;
    hasIncrementedViewRef.current = false;
    hasTrackedPlayRef.current = false;
    hasTrackedCompleteRef.current = false;
    hasTrackedAudiobookCompletedRef.current = false;
    lastStorybookProgressRef.current = { page: null, status: null };
  }, [audiobookId]);

  // Reset feedback state when audiobookId changes
  useEffect(() => {
    // Reset feedback modal visibility when switching audiobooks
    setShowFeedbackModal(false);
    setShowFeedbackNotification(false);

    // Clear any pending notification timeout
    if (feedbackNotificationTimeoutRef.current) {
      clearTimeout(feedbackNotificationTimeoutRef.current);
      feedbackNotificationTimeoutRef.current = null;
    }

    logger.log(`🔄 Feedback state reset for audiobook: ${audiobookId}`);
  }, [audiobookId]);

  useEffect(() => {
    setIsSpreadLayout(currentPageIndex > 0);
  }, [currentPageIndex]);

  // Mark a page's text reveal as played when the reader flips away from it.
  useEffect(() => {
    const prev = lastViewedPageIndexRef.current;
    if (prev !== currentPageIndex) {
      animatedTextPageIndexesRef.current.add(prev);
      lastViewedPageIndexRef.current = currentPageIndex;
    }
  }, [currentPageIndex]);

  // Increment audiobook view exactly once per page load
  useEffect(() => {
    // Guard: Prevent duplicate view increments
    if (hasIncrementedViewRef.current || !audiobookId || !apiClient) {
      return;
    }

    // Only increment view if audiobook is successfully loaded (not processing)
    if (!audiobook) {
      return; // Wait for audiobook to load first
    }

    const status = audiobook.status?.toLowerCase();
    const isProcessing = status === "creating" || status === "processing";

    // Don't increment views for processing audiobooks
    if (isProcessing) {
      return;
    }

    // Mark as incremented to prevent duplicate calls
    hasIncrementedViewRef.current = true;
    console.log("📊 POST /audiobooks/{id}/increment-view called");

    // Call increment-view endpoint (non-blocking, fire and forget)
    apiClient
      .incrementAudiobookView(audiobookId)
      .then((response) => {
        if (response.status) {
          console.log("✅ Audiobook view incremented successfully");
        } else {
          console.warn("⚠️ View increment response:", response);
        }
      })
      .catch((error) => {
        // Silently fail - view increment is not critical
        console.error("❌ Error incrementing audiobook view:", error);
      });
  }, [audiobook, audiobookId, apiClient]);

  // Poll while creating/processing, or while progressively generating remaining
  // pages after the book is already readable (status=ready, generation_complete=false).
  useEffect(() => {
    if (!audiobook || !audiobookId || !apiClient) return;

    // NOT during a live voice session: the WebSocket is the source of truth
    // there, and this poll's wholesale setAudiobook(response.data) wiped the
    // just-flipped pending text page every 3s (the DB only gets a page after
    // its illustration finishes rendering) — pages vanished until page_created.
    if (isVoiceLive) return;

    const status = audiobook.status?.toLowerCase();
    const isProcessing = status === "creating" || status === "processing";
    // Legacy books omit generation_complete → treat as complete.
    const generationComplete =
      audiobook.generation_complete == null
        ? true
        : Boolean(audiobook.generation_complete);
    const isProgressiveGenerating =
      (status === "ready" || status === "completed") && !generationComplete;

    if (!isProcessing && !isProgressiveGenerating) return;

    const pollInterval = setInterval(async () => {
      try {
        const response = await apiClient.getAudiobook(audiobookId, accessToken, true);
        if (response.status && response.data) {
          setAudiobook(response.data);
        }
      } catch (err) {
        logger.error("Error polling audiobook:", err);
      }
    }, 3000); // Poll every 3 seconds

    return () => clearInterval(pollInterval);
  }, [audiobook, audiobookId, apiClient, accessToken, isVoiceLive]);

  // Handle page change - stop previous audio and update the page. When continuing
  // (auto-advance / "read to me"), flag the new page to auto-play. The actual source
  // load + playback is owned solely by the audio-source effect below, so we never
  // race two load() calls (which was aborting play() and stopping narration).
  const handlePageChange = useCallback((newIndex, shouldAutoPlay = false) => {
    const audio = audioRef.current;
    if (audio) {
      audio.pause();
      audio.currentTime = 0;
    }
    setCurrentTime(0);
    setDuration(0);
    setCurrentPageIndex(newIndex);
    trackAudiobookPageTurned(audiobookId, newIndex, displayPagesRef.current?.length || 0);

    if (shouldAutoPlay) {
      autoPlayNextRef.current = true;
      setIsContinuousPlay(true);
    } else {
      autoPlayNextRef.current = false;
      setIsPlaying(false);
    }
  }, [audiobookId]);

  // Handle audiobook completion - per-audiobook feedback logic
  const handleAudiobookEnd = useCallback(() => {
    // Never interrupt an in-progress AI voice creation session.
    if (isVoiceLive) {
      logger.log("🎙️ Voice session live — skipping feedback prompt");
      return;
    }

    // Progressive generation: first batch can look like "end" (e.g. 4/8 pages)
    // while generation_complete is still false — wait for the real finale.
    const generationComplete =
      audiobook?.generation_complete == null
        ? true
        : Boolean(audiobook.generation_complete);
    if (!generationComplete) {
      logger.log(
        "⏳ Progressive generation incomplete — skipping feedback prompt",
      );
      return;
    }
    const targetPages = Number(audiobook?.total_pages) || 0;
    const readyPages = Array.isArray(audiobook?.pages)
      ? audiobook.pages.length
      : 0;
    if (targetPages > 0 && readyPages < targetPages) {
      logger.log(
        `⏳ Only ${readyPages}/${targetPages} pages ready — skipping feedback prompt`,
      );
      return;
    }

    trackAudiobookCompleted(audiobookId, audiobook?.title);
    if (!user?.id || !audiobookId) {
      logger.log("👤 Guest user or no audiobookId: no feedback modal shown");
      return;
    }

    // Check if THIS audiobook has been dismissed
    const isDismissed = getFeedbackDismissed(audiobookId);

    if (isDismissed) {
      logger.log(
        `🔕 Feedback already dismissed for audiobook ${audiobookId}, skipping`,
      );
      return;
    }

    // Check if modal is already open
    if (showFeedbackModal || showFeedbackNotification) {
      logger.log("🔔 Feedback modal already open, skipping trigger");
      return;
    }

    logger.log(
      `🎧 Audiobook ${audiobookId} completed. Checking feedback eligibility`,
    );

    // Check if user has seen feedback modal globally (first-time vs returning user)
    const userDataTags = user.data_tags || [];
    const hasSeenGlobally = userDataTags.some((tag) =>
      tag.startsWith("FEEDBACK_MODAL_SHOWN"),
    );

    if (hasSeenGlobally) {
      // Returning user: show notification variant (2s delay)
      logger.log("🔔 Returning user: showing feedback notification (2s delay)");

      if (feedbackNotificationTimeoutRef.current) {
        clearTimeout(feedbackNotificationTimeoutRef.current);
      }

      feedbackNotificationTimeoutRef.current = setTimeout(() => {
        if (!showFeedbackModal && !getFeedbackDismissed(audiobookId)) {
          setShowFeedbackNotification(true);
          logger.log("📬 Small feedback notification shown");
        }
        feedbackNotificationTimeoutRef.current = null;
      }, 2000);
    } else {
      // First-time user: show full modal
      logger.log("🆕 First-time user: showing full feedback modal");
      setShowFeedbackModal(true);

      // Persist global tag to backend (async)
      apiClient
        .addUserDataTag(user.id, "FEEDBACK_MODAL_SHOWN")
        .then((result) => {
          if (result.status) {
            logger.log(
              "✅ FEEDBACK_MODAL_SHOWN successfully added to users.data_tags",
            );
          } else {
            logger.warn("⚠️ Failed to add to users.data_tags:", result.error);
          }
        })
        .catch((error) => {
          logger.error("❌ Error adding to users.data_tags:", error);
        });
    }
  }, [
    user,
    apiClient,
    audiobookId,
    audiobook?.title,
    audiobook?.generation_complete,
    audiobook?.total_pages,
    audiobook?.pages,
    isVoiceLive,
    showFeedbackModal,
    showFeedbackNotification,
    trackAudiobookCompleted,
  ]);

  // Get creator name and visibility
  const isOwner = user?.id === audiobook?.user_id;
  const isPublic = audiobook?.is_public;

  // Show creator name only for public content or if you're the owner
  const shouldShowCreator = isPublic || isOwner;
  const creatorName = isOwner
    ? user?.username || user?.name || "You"
    : audiobook?.creator_name || audiobook?.username || "Creator";

  // Get current page (use translated if available)
  const rawStoryPages = translatedPages || audiobook?.pages || [];
  const hasRealPages = rawStoryPages.length > 0;
  // Before Gemini has streamed the first real page, show a friendly
  // "let's create your story together" spread inside the same open-book
  // layout instead of an empty reader — it's swapped out automatically the
  // instant handleVoicePageCreated appends a real page.
  const storyPages =
    isVoiceLive && !hasRealPages
      ? [{ type: "voice_intro", text: "", image_url: null }]
      : rawStoryPages;

  // Create cover page
  const coverPage = audiobook
    ? {
        type: "cover",
        image_url: audiobook.thumbnail_url || audiobook.cover_url,
        text: audiobook.title,
        creator: shouldShowCreator ? creatorName : null,
        audio_url: null, // No audio for cover page
      }
    : null;

  // Add cover page at the beginning, and a celebration end page after the
  // last story page (mirrors the iOS post-completion prompt). Skipped while
  // a live voice session is still streaming pages in. While the book is
  // still generating (progressive flow), the trailing page is a friendly
  // "still being written" page instead of The End — the finale only shows
  // once generation_complete flips (legacy books without the flag = done).
  const bookGenerationComplete =
    audiobook?.generation_complete == null
      ? true
      : Boolean(audiobook.generation_complete);
  const endPage =
    storyPages.length > 0 && !isVoiceLive
      ? [{ type: bookGenerationComplete ? "end" : "generating" }]
      : [];
  const displayPages = coverPage
    ? [coverPage, ...storyPages, ...endPage]
    : [...storyPages, ...endPage];
  const currentPage = displayPages?.[currentPageIndex] || null;
  displayPagesRef.current = displayPages; // keep latest pages available to stable callbacks

  useEffect(() => {
    if (!apiClient || !user?.id || !audiobook?.id) return;
    if (isVoiceLive && !hasRealPages) return; // synthetic placeholder page, nothing to persist yet

    const totalPages = Array.isArray(storyPages) ? storyPages.length : 0;
    if (totalPages <= 0) return;

    // Skip cover page and invalid states
    if (currentPageIndex <= 0) return;

    const zeroBasedPage = clampStorybookResumePage(currentPageIndex - 1, totalPages);
    const oneBasedPage = zeroBasedPage + 1;
    // Don't mark FINISHED on the last *ready* page while more pages are still generating.
    const generationComplete =
      audiobook?.generation_complete == null
        ? true
        : Boolean(audiobook.generation_complete);
    const targetPages =
      Number(audiobook?.total_pages) > 0
        ? Number(audiobook.total_pages)
        : totalPages;
    const status =
      generationComplete && oneBasedPage >= targetPages
        ? "FINISHED"
        : "IN_PROGRESS";

    const previous = lastStorybookProgressRef.current;
    if (previous.page === oneBasedPage && previous.status === status) return;

    lastStorybookProgressRef.current = { page: oneBasedPage, status };
    updateStorybookProgress(apiClient, user.id, audiobook.id, oneBasedPage, status)
      .then(async (didUpdate) => {
        if (!didUpdate) return;

        try {
          const statsResponse = await apiClient.getUserStats(user.id);
          if (statsResponse?.status && statsResponse?.data) {
            updateUser({
              ...user,
              stats: statsResponse.data,
            });
          }
        } catch (error) {
          logger.warn("Failed to refresh user stats after storybook progress update", error);
        }
      })
      .catch((error) => {
        logger.warn("Failed to persist storybook progress update", error);
      });
  }, [apiClient, user?.id, audiobook?.id, audiobook?.generation_complete, audiobook?.total_pages, currentPageIndex, storyPages, isVoiceLive, hasRealPages]);

  // Preload images for next 3 pages to avoid loading delays
  useEffect(() => {
    if (!displayPages || displayPages.length === 0) return;

    const pagesToPreload = [];
    // Preload next 3 pages
    for (let i = 1; i <= 3; i++) {
      const nextIndex = currentPageIndex + i;
      if (nextIndex < displayPages.length) {
        const page = displayPages[nextIndex];
        if (page?.image_url) {
          pagesToPreload.push(page.image_url);
        }
      }
    }

    // Preload images using Image constructor
    pagesToPreload.forEach(url => {
      const img = new Image();
      img.src = url;
    });
  }, [currentPageIndex, displayPages]);

  // Send one event per action (play/pause/seek/completed). Backend expects event_type + optional metadata.
  const logAudioEvent = useCallback(
    (eventType, metadata = {}) => {
      if (!user?.id || !audiobookId || !audiobookSessionId.current || !apiClient) {
        logger.warn("📊 logAudioEvent: skip — user not logged in or audiobook/session not ready");
        return;
      }
      try {
        apiClient
          .sendAudiobookEvent(user.id, audiobookId, audiobookSessionId.current, eventType, metadata)
          .catch((err) => {
            if (process.env.NODE_ENV === "development") logger.warn("📊 sendAudiobookEvent failed", err);
          });
      } catch (err) {
        if (process.env.NODE_ENV === "development") logger.warn("📊 logAudioEvent error", err);
      }
    },
    [user?.id, audiobookId, apiClient],
  );

  // Audio event handlers
  useEffect(() => {
    const audio = audioRef.current;
    if (!audio || !currentPage?.audio_url) return;

    const handleTimeUpdate = () => {
      setCurrentTime(audio.currentTime);
    };

    const handleLoadedMetadata = () => {
      setDuration(audio.duration || currentPage.duration || 0);
      // Set playback rate after metadata loads (load() resets it)
      audio.playbackRate = playbackRate;
    };

    const handlePlay = () => {
      logAudioEvent("play");
      setIsPlaying(true);
      trackAudiobookPlayed(audiobookId, audiobook?.title);
    };

    const handlePause = () => {
      logAudioEvent("pause");
      setIsPlaying(false);
    };

    const handleSeeking = () => {
      seekFromTimeRef.current = audio.currentTime;
    };

    const handleSeeked = () => {
      const fromSec = seekFromTimeRef.current;
      const toSec = audio.currentTime;
      const meta = { from_sec: Math.floor(fromSec), to_sec: Math.floor(toSec) };
      if (typeof currentPageIndex === "number") meta.page_index = currentPageIndex;

      if (pendingSkipRef.current) {
        const { type, from } = pendingSkipRef.current;
        pendingSkipRef.current = null;
        logAudioEvent(type, { from_sec: Math.floor(from), to_sec: Math.floor(toSec), ...(typeof currentPageIndex === "number" && { page_index: currentPageIndex }) });
      } else {
        logAudioEvent("seek", meta);
      }
    };

    const handleEnded = () => {
      lastCurrentTimeBeforeZeroRef.current = 0; // so autoplay after completion does not fire replay
      logAudioEvent("completed"); // page-level completion (every page)
      setCurrentTime(0);

      // audiobook_completed = full audiobook completion (analytics); send only once
      // per session when the *final* story page ends — not mid progressive batch.
      const generationComplete =
        audiobook?.generation_complete == null
          ? true
          : Boolean(audiobook.generation_complete);
      const targetStoryPages =
        Number(audiobook?.total_pages) > 0
          ? Number(audiobook.total_pages)
          : Math.max(0, displayPages.length - (displayPages[0]?.type === "cover" ? 1 : 0));
      const readyStoryPages = Math.max(
        0,
        displayPages.length - (displayPages[0]?.type === "cover" ? 1 : 0),
      );
      const isLastPage =
        generationComplete &&
        readyStoryPages >= targetStoryPages &&
        currentPageIndex === displayPages.length - 1;
      if (isLastPage && !hasTrackedAudiobookCompletedRef.current) {
        hasTrackedAudiobookCompletedRef.current = true;
        logAudioEvent("audiobook_completed");
      }

      // Auto-advance if in continuous play mode (use displayPages.length to account for cover page)
      if (isContinuousPlay && currentPageIndex < displayPages.length - 1) {
        // Move to next page
        handlePageChange(currentPageIndex + 1, true);
      } else {
        // End of book or not in continuous mode
        setIsPlaying(false);
        setIsContinuousPlay(false);
        // Stay on current page - don't auto-navigate
        // User can press play again to restart from beginning
      }
    };

    const handleCanPlay = () => {
      setDuration(audio.duration || currentPage.duration || 0);
      // Ensure playback rate is set
      audio.playbackRate = playbackRate;
    };

    const handleError = (e) => {
      logger.error("Audio playback error:", e);
      setIsPlaying(false);
      showToast("Error playing audio", "error");
    };

    audio.addEventListener("timeupdate", handleTimeUpdate);
    audio.addEventListener("loadedmetadata", handleLoadedMetadata);
    audio.addEventListener("play", handlePlay);
    audio.addEventListener("pause", handlePause);
    audio.addEventListener("seeking", handleSeeking);
    audio.addEventListener("seeked", handleSeeked);
    audio.addEventListener("ended", handleEnded);
    audio.addEventListener("canplay", handleCanPlay);
    audio.addEventListener("error", handleError);

    return () => {
      audio.removeEventListener("timeupdate", handleTimeUpdate);
      audio.removeEventListener("loadedmetadata", handleLoadedMetadata);
      audio.removeEventListener("play", handlePlay);
      audio.removeEventListener("pause", handlePause);
      audio.removeEventListener("seeking", handleSeeking);
      audio.removeEventListener("seeked", handleSeeked);
      audio.removeEventListener("ended", handleEnded);
      audio.removeEventListener("canplay", handleCanPlay);
      audio.removeEventListener("error", handleError);
    };
  }, [
    currentPage?.audio_url,
    currentPageIndex,
    displayPages.length,
    displayPages,
    handlePageChange,
    isContinuousPlay,
    playbackRate,
    logAudioEvent,
    audiobook?.generation_complete,
    audiobook?.total_pages,
  ]);

  // Single owner of the audio source: keep the element pointed at the current page,
  // and — when an auto-play is pending (continuous play / "read to me" advance) —
  // start playback once THIS page's source can play. The pending flag is cleared
  // only when playback actually starts, so an effect re-run can't drop the trigger,
  // and because this is the ONLY place that calls load(), play() is never aborted.
  useEffect(() => {
    const audio = audioRef.current;
    if (!audio || !currentPage?.audio_url) return;

    const needsLoad = audio.src !== currentPage.audio_url;
    if (needsLoad) {
      audio.src = currentPage.audio_url;
      audio.load();
    }
    // Playback rate is (re)applied in the loadedmetadata/canplay handlers.

    if (!autoPlayNextRef.current) return;

    const playWhenReady = () => {
      audio.removeEventListener("canplay", playWhenReady);
      if (!autoPlayNextRef.current) return;
      autoPlayNextRef.current = false;
      audio
        .play()
        .then(() => setIsPlaying(true))
        .catch((err) => {
          // AbortError is benign (a newer load()/play() superseded this one) — don't
          // tear down playback/continuous mode over it, or auto-advance silently dies.
          if (err && err.name === "AbortError") return;
          logger.error("Auto-play failed:", err);
          setIsPlaying(false);
          setIsContinuousPlay(false);
        });
    };

    // After a load(), the source is NOT ready — wait for `canplay`. Calling play()
    // synchronously here would be aborted by the in-flight load() (which was killing
    // narration on page advance). Only play immediately when nothing is loading.
    if (!needsLoad && audio.readyState >= 3) {
      playWhenReady();
    } else {
      audio.addEventListener("canplay", playWhenReady);
    }
    return () => audio.removeEventListener("canplay", playWhenReady);
  }, [currentPage?.audio_url]);

  // Update playback rate when it changes
  useEffect(() => {
    if (audioRef.current) {
      audioRef.current.playbackRate = playbackRate;
    }
  }, [playbackRate]);

  // Trigger feedback modal only on the true story end (not mid progressive batch).
  useEffect(() => {
    // Only trigger if:
    // 1. Audiobook is loaded and ready
    // 2. User is authenticated
    // 3. Generation is fully complete (or legacy book without the flag)
    // 4. User is on the last *full* page (total_pages), not just last ready page
    // 5. Feedback modal hasn't been shown yet (cache guard)
    // Never during AI voice creation — the "last" page keeps changing as
    // pages stream in, and the intro placeholder is also the last page.
    if (isVoiceLive) return;
    if (!displayPages || displayPages.length === 0) return;
    if (!user?.id) return; // Only for authenticated users
    if (!audiobook) return;

    const generationComplete =
      audiobook.generation_complete == null
        ? true
        : Boolean(audiobook.generation_complete);
    if (!generationComplete) {
      logger.log(
        "⏳ Progressive generation incomplete — not treating as story end for feedback",
      );
      return;
    }

    const targetStoryPages =
      Number(audiobook.total_pages) > 0
        ? Number(audiobook.total_pages)
        : storyPages.length;
    if (storyPages.length < targetStoryPages) {
      logger.log(
        `⏳ Waiting for all pages (${storyPages.length}/${targetStoryPages}) before feedback`,
      );
      return;
    }

    const isLastPage = currentPageIndex === displayPages.length - 1;

    if (!isLastPage) return;

    // Add COMPLETEAUDIOBOOK tag when user reaches last page (once per audiobook view)
    if (!hasTrackedCompleteRef.current) {
      hasTrackedCompleteRef.current = true;
      const timestamp = new Date().toISOString();
      const tag = `COMPLETEAUDIOBOOK_${audiobookId}_${timestamp}`;
      apiClient
        .addUserDataTag(user.id, tag)
        .then((result) => {
          if (result.status) {
            logger.log(
              `✅ Added complete audiobook tag ${tag} for user ${user.id}`,
            );
          } else {
            logger.warn(
              `⚠️ Failed to add complete audiobook tag ${tag}:`,
              result.error,
            );
          }
        })
        .catch((error) => {
          logger.error(`❌ Error adding complete audiobook tag ${tag}:`, error);
        });
    }

    // Guard: Check if THIS audiobook has been dismissed
    const isDismissed = getFeedbackDismissed(audiobookId);
    if (isDismissed) {
      logger.log(
        `🔕 Feedback dismissed for audiobook ${audiobookId}, skipping trigger`,
      );
      return;
    }

    // Guard: Don't trigger if modal is already open
    if (showFeedbackModal || showFeedbackNotification) {
      logger.log(
        "🔔 Feedback modal already open, skipping trigger on last page",
      );
      return;
    }

    logger.log(
      `🎧 User reached last page of audiobook ${audiobookId} - triggering feedback modal check`,
    );
    handleAudiobookEnd();
  }, [
    currentPageIndex,
    displayPages,
    storyPages.length,
    user,
    audiobook,
    audiobookId,
    apiClient,
    isVoiceLive,
    showFeedbackModal,
    showFeedbackNotification,
    handleAudiobookEnd,
  ]);

  // If a feedback chip somehow opened, dismiss it while creating via voice.
  useEffect(() => {
    if (!isVoiceLive) return;
    if (feedbackNotificationTimeoutRef.current) {
      clearTimeout(feedbackNotificationTimeoutRef.current);
      feedbackNotificationTimeoutRef.current = null;
    }
    if (showFeedbackNotification) setShowFeedbackNotification(false);
    if (showFeedbackModal) setShowFeedbackModal(false);
  }, [isVoiceLive, showFeedbackNotification, showFeedbackModal]);

  const togglePlayPause = useCallback(async () => {
    const audio = audioRef.current;

    // No narration on this page (e.g. the cover) → start narration from the first story page.
    if (!currentPage?.audio_url || currentPageIndex === 0) {
      setIsContinuousPlay(true); // Enable before page change
      handlePageChange(1, true);
      return;
    }

    // Book finished (played all the way through the last page) → restart from the beginning.
    // Only when the audio has actually ENDED — a normal pause on the last page must resume
    // from where it stopped, not jump back to page 1.
    if (currentPageIndex === displayPages.length - 1 && audio && audio.ended) {
      setIsContinuousPlay(true);
      handlePageChange(1, true);
      return;
    }

    try {
      if (audio.paused) {
        // Replay = user restarts from beginning after having listened >5s (e.g. after Stop)
        if (audio.currentTime === 0 && lastCurrentTimeBeforeZeroRef.current > 5) {
          logAudioEvent("replay");
          lastCurrentTimeBeforeZeroRef.current = 0;
        }
        // pause() does not reset currentTime, so this resumes from the exact spot.
        await audio.play();
        setIsPlaying(true);
        setIsContinuousPlay(true); // Enable continuous play mode
      } else {
        audio.pause();
        setIsPlaying(false);
        // Keep continuous mode on when pausing (so it resumes properly)
      }
    } catch (error) {
      logger.error("Error toggling playback:", error);
      setIsPlaying(!audio.paused);
      showToast("Unable to play audio. Please try again.", "error");
    }
  }, [currentPage?.audio_url, currentPageIndex, displayPages.length, handlePageChange, logAudioEvent]);

  const handleStop = useCallback(() => {
    const audio = audioRef.current;
    if (audio) {
      lastCurrentTimeBeforeZeroRef.current = audio.currentTime; // for replay detection (user restarts after stop)
      audio.pause();
      audio.currentTime = 0;
    }
    setIsPlaying(false);
    setIsContinuousPlay(false);
    setCurrentTime(0);
  }, []);

  const handleSeek = (e) => {
    if (audioRef.current && duration) {
      const rect = e.currentTarget.getBoundingClientRect();
      const clickX = e.clientX - rect.left;
      const newTime = (clickX / rect.width) * duration;
      audioRef.current.currentTime = newTime;
    }
  };

  // For future skip buttons (+10s / -10s): sets pendingSkipRef then currentTime; seeked fires and sends skip_forward/skip_backward.
  const jumpTo = useCallback((secondsDelta, { skipForward = false, skipBackward = false } = {}) => {
    const audio = audioRef.current;
    if (!audio) return;
    const from = audio.currentTime;
    if (skipForward) {
      pendingSkipRef.current = { type: "skip_forward", from };
      audio.currentTime = Math.min(audio.duration || Infinity, from + secondsDelta);
    } else if (skipBackward) {
      pendingSkipRef.current = { type: "skip_backward", from };
      audio.currentTime = Math.max(0, from - secondsDelta);
    }
  }, []);

  const handleVolumeChange = (e) => {
    const newVolume = parseFloat(e.target.value);
    setVolume(newVolume);
    if (audioRef.current) {
      audioRef.current.volume = newVolume;
    }
  };

  const formatTime = (seconds) => {
    if (!seconds || isNaN(seconds)) return "0:00";
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, "0")}`;
  };

  if (loading) {
    return <LoadingScreen message="Loading storybook..." />;
  }

  if (error || !audiobook) {
    return (
      <div
        className="min-h-screen relative flex items-center justify-center px-4"
        style={{
          background: "linear-gradient(180deg, #F5FAFF 0%, #EAF3FF 100%)",
          fontFamily: "'Inter', 'Neue Montreal', 'Fredoka', sans-serif",
        }}
      >
        {/* Soft brand blobs */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div style={{ position: "absolute", top: -180, left: -160, width: 460, height: 460, borderRadius: "50%", background: "rgba(0,101,248,0.08)", filter: "blur(40px)" }} />
          <div style={{ position: "absolute", bottom: -200, right: -180, width: 480, height: 480, borderRadius: "50%", background: "rgba(0,202,255,0.08)", filter: "blur(40px)" }} />
        </div>

        <style>{`
          @keyframes pipFloat { 0%,100%{ transform: translateY(0) rotate(-6deg);} 50%{ transform: translateY(-12px) rotate(-6deg);} }
          @keyframes pipShadow { 0%,100%{ transform: scaleX(1); opacity:.34;} 50%{ transform: scaleX(.8); opacity:.2;} }
          @keyframes pipDot { 0%,100%{ transform: translateY(0); opacity:.5;} 50%{ transform: translateY(-8px); opacity:1;} }
        `}</style>

        <div className="relative z-10 text-center" style={{ maxWidth: 480 }}>
          {/* Sad Pip the panda */}
          <div className="relative mx-auto" style={{ width: 200, height: 200 }}>
            {/* floating sparkle dots for a touch of whimsy */}
            <span style={{ position: "absolute", top: 8, left: 18, width: 9, height: 9, borderRadius: "50%", background: "#8FC0FF", animation: "pipDot 2.6s ease-in-out infinite" }} />
            <span style={{ position: "absolute", top: 30, right: 14, width: 7, height: 7, borderRadius: "50%", background: "#B9E0FF", animation: "pipDot 3.1s ease-in-out .4s infinite" }} />

            <div style={{ position: "absolute", inset: 0, borderRadius: "50%", background: "#FFFFFF", boxShadow: "0px 2px 0px rgba(11,26,51,0.06), 0px 18px 44px rgba(11,26,51,0.13)", border: "2px solid rgba(11,26,51,0.06)" }} />
            <img
              src="/assets/pixley-panda-nobg.png"
              alt="Pip the panda looking sad"
              style={{ position: "absolute", inset: 0, width: "100%", height: "100%", objectFit: "contain", padding: 26, filter: "grayscale(0.3) saturate(0.85)", animation: "pipFloat 3.4s ease-in-out infinite" }}
            />
            {/* lock badge — this storybook is private */}
            <div style={{ position: "absolute", right: 4, bottom: 6, width: 54, height: 54, borderRadius: "50%", background: "#FFFFFF", border: "3px solid #EAF3FF", display: "flex", alignItems: "center", justifyContent: "center", boxShadow: "0px 4px 12px rgba(11,26,51,0.18)", fontSize: 24 }}>🔒</div>
          </div>

          {/* little floor shadow */}
          <div style={{ width: 128, height: 15, margin: "6px auto 26px", borderRadius: "50%", background: "rgba(11,26,51,0.18)", filter: "blur(7px)", animation: "pipShadow 3.4s ease-in-out infinite" }} />

          <h1 style={{ fontFamily: "'Fredoka', sans-serif", fontSize: 30, fontWeight: 700, color: "#0B1A33", marginBottom: 12 }}>
            Storybook Not Found
          </h1>
          <p style={{ color: "#5A6B85", fontSize: 16, lineHeight: 1.55, marginBottom: 28 }}>
            {error ||
              "This storybook is private or needs a valid share link — so Pip couldn't open it. Try heading back to your library!"}
          </p>
          <button
            onClick={() => navigate("/library")}
            className="inline-flex items-center gap-2 transition active:scale-[0.97]"
            style={{ fontFamily: "'Fredoka', sans-serif", fontWeight: 700, fontSize: 16, color: "#FFFFFF", background: "#0065F8", border: "none", borderRadius: 999, padding: "14px 28px", boxShadow: "0px 4px 0px #073B8E", cursor: "pointer" }}
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth={2.4} viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
            </svg>
            Back to Storybook Library
          </button>
        </div>
      </div>
    );
  }

  const isReady =
    audiobook.status?.toLowerCase() === "completed" ||
    audiobook.status?.toLowerCase() === "ready";
  const isProcessing =
    audiobook.status?.toLowerCase() === "creating" ||
    audiobook.status?.toLowerCase() === "processing";
  const isFailed = audiobook.status?.toLowerCase() === "failed";
  const generationComplete =
    audiobook.generation_complete == null
      ? true
      : Boolean(audiobook.generation_complete);
  const isProgressiveGenerating = isReady && !generationComplete;
  const totalPagesTarget =
    audiobook.total_pages ||
    audiobook.pages_ready_count ||
    audiobook.pages?.length ||
    0;
  const progress = audiobook.progress || audiobook.percentage || 0;
  // While an AI Voice Storybook conversation is live, show the book even if
  // backend status is still creating/processing. Once the backend marks
  // ready/complete (including auto-finalize after disconnect), isVoiceLive
  // is false and the normal reader takes over.
  const hasPages = hasRealPages || isVoiceLive;
  const isBookOnScreen = (isReady || isVoiceLive) && hasPages;

  const isReadingSpread = isReady && hasPages && isSpreadLayout;
  const canNavigatePrev = currentPageIndex > 0;
  const readyPageCount = audiobook.pages?.length || 0;
  const canNavigateNext = currentPageIndex < readyPageCount - 1;
  const waitingForNextPage =
    isProgressiveGenerating &&
    readyPageCount > 0 &&
    currentPageIndex >= readyPageCount - 1 &&
    !canNavigateNext;

  // Handle visibility toggle (owner only)
  const handleVisibilityToggle = async () => {
    if (!isOwner || !audiobook || isUpdatingVisibility) return;

    const newVisibility = !audiobook.is_public;

    try {
      setIsUpdatingVisibility(true);
      const response = await apiClient.updateAudiobookVisibility(
        audiobook.id,
        newVisibility,
      );

      if (response.status) {
        trackVisibilityToggled('audiobook', audiobook.id, newVisibility);
        // Optimistically update UI
        setAudiobook({ ...audiobook, is_public: newVisibility });
        showToast(
          newVisibility
            ? "Storybook is now public"
            : "Storybook is now private",
          "success",
        );
      } else {
        showToast(response.error || "Failed to update visibility", "error");
      }
    } catch (error) {
      logger.error("Error updating audiobook visibility:", error);
      showToast("Failed to update visibility", "error");
    } finally {
      setIsUpdatingVisibility(false);
    }
  };

  // Handle video download
  // Downloads are a premium-only feature (mirrors StoryDetail). Free/anonymous
  // users see the paywall instead. The backend also enforces this, so this is
  // purely UX — it can't be bypassed by skipping the client check.
  const canDownload = isAuthenticated && costs?.downloadAbility === true;

  const handleDownloadVideo = async () => {
    logger.log("🎬 Download video button clicked");

    if (!requireEntitlement({ trigger: "download", contentType: "storybook" })) {
      return;
    }
    if (!canDownload) {
      logger.log("Non-premium/anonymous user attempting storybook download - showing paywall");
      setIsPaywallModalOpen(true);
      return;
    }

    trackContentDownloaded('audiobook', audiobookId);

    if (!audiobook) {
      logger.error("No audiobook data available");
      showToast("Storybook not loaded", "error");
      return;
    }

    const downloadVideoFile = async (videoUrl) => {
      logger.log(`📥 Downloading video from: ${videoUrl}`);
      try {
        const videoResponse = await fetch(videoUrl, { mode: "cors" });
        if (!videoResponse.ok) {
          throw new Error(`HTTP ${videoResponse.status}`);
        }
        const videoBlob = await videoResponse.blob();
        const blobUrl = URL.createObjectURL(videoBlob);

        const link = document.createElement("a");
        link.href = blobUrl;
        link.download = `${audiobook.title || "storybook"}.mp4`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);

        setTimeout(() => URL.revokeObjectURL(blobUrl), 100);
        logger.log("✅ Download triggered successfully");
      } catch (err) {
        logger.error("Failed to download video:", err);
        window.open(videoUrl, "_blank");
      }
    };

    try {
      logger.log(`📹 Requesting video for audiobook ${audiobook.id}`);

      const response = await apiClient.generateAudiobookVideo(audiobook.id);
      logger.log("Video generation response:", response);

      if (response.status === "ready" && response.video_url) {
        // Video already exists (cached) - download immediately
        logger.log(`✅ Video already exists: ${response.video_url}`);
        showToast("✨ Downloading...", "success");
        await downloadVideoFile(response.video_url);
        setAudiobook({ ...audiobook, video_url: response.video_url });
      } else if (response.status === "generating") {
        // Video is being generated - start background polling
        logger.log("⏳ Video generation in progress, starting background polling...");

        // Register with video download manager (polling continues even if user navigates away)
        // The VideoDownloadNotification component will handle the actual download
        videoDownloadManager.startVideoGeneration(
          audiobook.id,
          audiobook.title,
          apiClient,
          (videoUrl) => {
            // This callback runs when video is ready - just update state, don't download
            // (VideoDownloadNotification component handles the download)
            setAudiobook({ ...audiobook, video_url: videoUrl });
          }
        );
      } else {
        logger.error("Unexpected response:", response);
        showToast("Failed to start video generation", "error");
      }
    } catch (error) {
      logger.error("❌ Error requesting video:", error);
      // Backend enforces premium too; if it rejects (403), show the paywall.
      if (error?.status === 403) {
        setIsPaywallModalOpen(true);
      } else {
        showToast("Failed to request video generation", "error");
      }
    }
  };

  const FREDOKA = { fontFamily: "'Fredoka', sans-serif" };

  return (
    <div
      className={`min-h-screen px-4 sm:px-6 lg:px-8 relative ${isBookOnScreen ? "pt-24" : "pt-32"}`}
      style={{
        background: isReadingSpread ? "#eef2f7" : "linear-gradient(180deg, #F5FAFF 0%, #EAF3FF 100%)",
        fontFamily: "'Inter', 'Fredoka', sans-serif",
        paddingBottom: isPlaying || isContinuousPlay ? "140px" : isReadingSpread ? "32px" : "64px",
        transition: "background 0.35s ease, padding 0.35s ease",
      }}
    >
      {/* Soft brand blobs */}
      {!isReadingSpread && (
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div style={{ position: "absolute", top: -200, left: -180, width: 520, height: 520, borderRadius: "50%", background: "rgba(0,101,248,0.08)", filter: "blur(30px)" }} />
        <div style={{ position: "absolute", bottom: -240, right: -220, width: 560, height: 560, borderRadius: "50%", background: "rgba(0,202,255,0.08)", filter: "blur(30px)" }} />
        <div style={{ position: "absolute", top: "30%", right: "8%", width: 220, height: 220, borderRadius: "50%", background: "rgba(255,107,154,0.06)", filter: "blur(28px)" }} />
      </div>
      )}

      <div className={`relative z-10 mx-auto ${isReadingSpread ? "max-w-[1280px]" : "max-w-7xl"}`}>
        {/* MOBILE HEADER */}
        <div className="mb-6 md:hidden flex flex-col gap-3">
          {/* Row 1: Back */}
          <div className="flex items-center justify-between">
            <button
              onClick={() => navigate("/library")}
              aria-label="Back"
              className="inline-flex items-center gap-1.5 transition active:scale-[0.97]"
              style={{
                ...FREDOKA,
                background: "#FFFFFF",
                color: "#0065F8",
                fontWeight: 700,
                fontSize: 13,
                border: "2px solid rgba(0,101,248,0.18)",
                borderRadius: 999,
                padding: "8px 14px",
                boxShadow: "0px 2px 0px rgba(11,26,51,0.06)",
                cursor: "pointer",
              }}
            >
              <svg width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2.6" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
              </svg>
              Storybooks
            </button>
            {audiobookId && isAuthenticated && (
              <LikeButton contentType="storybook" contentId={audiobookId} />
            )}
          </div>

          {/* Row 2: Title — only while the book itself isn't on screen
              (processing/failed); the cover carries the title once readable */}
          {!isBookOnScreen && (
            <div className="text-center">
              <span style={{ ...FREDOKA, color: "#0065F8", fontSize: 11, fontWeight: 700, letterSpacing: "0.24em", textTransform: "uppercase" }}>
                Storybook
              </span>
              <h1
                className="mt-1"
                style={{
                  ...FREDOKA,
                  color: "#2A3D6B",
                  fontWeight: 700,
                  fontSize: "clamp(1.35rem, 4.6vw, 2rem)",
                  letterSpacing: "-0.005em",
                  textTransform: "uppercase",
                  lineHeight: 1.1,
                  margin: 0,
                }}
              >
                {audiobook.title || "Untitled Storybook"}
              </h1>
              {shouldShowCreator && (
                <p style={{ ...FREDOKA, color: "#5A6B85", fontSize: 13, margin: "6px 0 0" }}>
                  by <strong style={{ color: "#0065F8" }}>{creatorName}</strong>
                </p>
              )}
            </div>
          )}

          {/* Row 3: Action chips */}
          <div className="flex items-center gap-2 flex-wrap justify-center">
            {isOwner && (
              <button
                onClick={handleVisibilityToggle}
                disabled={isUpdatingVisibility}
                className="inline-flex items-center gap-2 transition active:scale-[0.97]"
                style={{
                  ...FREDOKA,
                  background: "#FFFFFF",
                  color: "#0B1A33",
                  fontWeight: 700,
                  fontSize: 12,
                  border: `2px solid ${audiobook.is_public ? "rgba(16,185,129,0.45)" : "rgba(11,26,51,0.10)"}`,
                  borderRadius: 999,
                  padding: "6px 12px",
                  boxShadow: "0px 2px 0px rgba(11,26,51,0.05)",
                  cursor: "pointer",
                  opacity: isUpdatingVisibility ? 0.6 : 1,
                }}
              >
                <span style={{ width: 8, height: 8, borderRadius: "50%", background: audiobook.is_public ? "#10B981" : "#94A3B8" }} />
                {audiobook.is_public ? "Public" : "Private"}
              </button>
            )}
            <button
              onClick={handleOpenShare}
              className="inline-flex items-center gap-1.5 transition active:scale-[0.97]"
              style={{
                ...FREDOKA,
                background: "#FFFFFF",
                color: "#0B1A33",
                fontWeight: 700,
                fontSize: 12,
                border: "2px solid rgba(11,26,51,0.08)",
                borderRadius: 999,
                padding: "8px 14px",
                boxShadow: "0px 2px 0px rgba(11,26,51,0.06)",
                cursor: "pointer",
              }}
            >
              <svg width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2.4" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
              </svg>
              Copy
            </button>
            {hasPages && !isProcessing && (
              <>
                <button
                  onClick={(e) => { e.preventDefault(); e.stopPropagation(); setIsShareVideoModalOpen(true); }}
                  className="inline-flex items-center gap-1.5 transition active:scale-[0.97]"
                  style={{
                    ...FREDOKA,
                    background: "#FFFFFF",
                    color: "#0065F8",
                    fontWeight: 700,
                    fontSize: 12,
                    border: "2px solid rgba(0,101,248,0.22)",
                    borderRadius: 999,
                    padding: "8px 14px",
                    boxShadow: "0px 2px 0px rgba(11,26,51,0.06)",
                    cursor: "pointer",
                  }}
                >
                  <svg width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2.4" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.368 2.684 3 3 0 00-5.368-2.684z" />
                  </svg>
                  Share
                </button>
                <button
                  onClick={handleDownloadVideo}
                  className="inline-flex items-center gap-1.5 transition active:scale-[0.97]"
                  style={{
                    ...FREDOKA,
                    background: "#0065F8",
                    color: "#FFFFFF",
                    fontWeight: 700,
                    fontSize: 12,
                    border: "none",
                    borderRadius: 999,
                    padding: "8px 14px",
                    boxShadow: "0px 3px 0px #073B8E",
                    cursor: "pointer",
                  }}
                >
                  <svg width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2.6" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                  </svg>
                  {audiobook?.video_url ? "Ready" : "Video"}
                </button>
                {/* TEMP: PDF export test control */}
                <button
                  onClick={handleDownloadPdf}
                  disabled={isDownloadingPdf}
                  className="inline-flex items-center gap-1.5 transition active:scale-[0.97]"
                  style={{
                    ...FREDOKA,
                    background: "#0065F8",
                    color: "#FFFFFF",
                    fontWeight: 700,
                    fontSize: 12,
                    border: "none",
                    borderRadius: 999,
                    padding: "8px 14px",
                    boxShadow: "0px 3px 0px #073B8E",
                    cursor: isDownloadingPdf ? "wait" : "pointer",
                    opacity: isDownloadingPdf ? 0.75 : 1,
                  }}
                  title="Download PDF"
                >
                  <svg width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2.6" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                  </svg>
                  {isDownloadingPdf ? "…" : "PDF"}
                </button>
              </>
            )}
            <div ref={languageDropdownMobileRef} className="relative">
              <button
                onClick={(e) => { e.stopPropagation(); setShowLanguageDropdown(!showLanguageDropdown); }}
                disabled={isTranslating}
                className="inline-flex items-center gap-1.5 transition active:scale-[0.97]"
                style={{
                  ...FREDOKA,
                  background: "#FFFFFF",
                  color: "#0B1A33",
                  fontWeight: 700,
                  fontSize: 12,
                  border: "2px solid rgba(11,26,51,0.08)",
                  borderRadius: 999,
                  padding: "8px 12px",
                  boxShadow: "0px 2px 0px rgba(11,26,51,0.06)",
                  cursor: "pointer",
                  opacity: isTranslating ? 0.6 : 1,
                }}
              >
                {isTranslating ? (
                  <div style={{ width: 14, height: 14, borderRadius: "50%", border: "2px solid rgba(0,101,248,0.20)", borderTopColor: "#0065F8", animation: "ab-spin 1s linear infinite" }} />
                ) : (
                  <span style={{ fontSize: 16 }}>{getCurrentLanguage().flag}</span>
                )}
                <span>{getCurrentLanguage().name}</span>
                <svg width="12" height="12" fill="none" stroke="currentColor" strokeWidth="2.6" viewBox="0 0 24 24" style={{ transform: showLanguageDropdown ? "rotate(180deg)" : "none", transition: "transform 200ms" }}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
                </svg>
              </button>
              {showLanguageDropdown && (
                <div
                  className="absolute right-0 mt-2 z-50 overflow-hidden"
                  style={{
                    top: "100%",
                    width: 200,
                    background: "#FFFFFF",
                    border: "2px solid rgba(11,26,51,0.08)",
                    borderRadius: 18,
                    boxShadow: "0px 5px 0px rgba(11,26,51,0.08), 0px 18px 36px rgba(11,26,51,0.16)",
                  }}
                >
                  <div className="py-1 max-h-64 overflow-y-auto">
                    {languageOptions.map((lang) => (
                      <button
                        key={lang.code}
                        onClick={async (e) => {
                          e.stopPropagation();
                          setSelectedLanguage(lang.code);
                          setShowLanguageDropdown(false);
                          await translateAudiobook(lang.code);
                        }}
                        className="w-full flex items-center gap-3 px-4 py-2.5 text-left transition-colors"
                        style={{
                          ...FREDOKA,
                          background: selectedLanguage === lang.code ? "rgba(0,101,248,0.08)" : "transparent",
                          color: "#0B1A33",
                          fontWeight: selectedLanguage === lang.code ? 700 : 500,
                          fontSize: 14,
                        }}
                      >
                        <span style={{ fontSize: 20 }}>{lang.flag}</span>
                        <span className="flex-1">{lang.name}</span>
                        {selectedLanguage === lang.code && (
                          <svg width="16" height="16" fill="none" stroke="#0065F8" strokeWidth="3" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                          </svg>
                        )}
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* DESKTOP HEADER */}
        {/* Desktop actions — floating top-right: primary CTA + everything else in a compact menu */}
        {/* top 40 = the book's top edge; right kept tight so Video/PDF sit clear of the cover */}
        <div className="hidden md:flex items-center gap-2 absolute z-30" style={isReadingSpread ? { top: 0, right: 12 } : { top: 40, right: 12 }}>
          {/* Toolbar stays visible in the open-book spread too — readers kept
              losing Download/Share/Like the moment they left the cover. */}
          {(
            <>
              {hasPages && !isProcessing && (
                <>
                  <button
                    onClick={handleDownloadVideo}
                    className="inline-flex items-center gap-2 transition active:scale-[0.97]"
                    style={{
                      ...FREDOKA,
                      background: "#0065F8",
                      color: "#FFFFFF",
                      fontWeight: 700,
                      fontSize: 13,
                      border: "none",
                      borderRadius: 999,
                      padding: "10px 18px",
                      boxShadow: "0px 4px 0px #073B8E, 0px 10px 22px rgba(0,101,248,0.20)",
                      cursor: "pointer",
                    }}
                    title={audiobook.video_url ? "Download video (already generated)" : "Generate and download video (1-6 minutes)"}
                  >
                    <svg width="15" height="15" fill="none" stroke="currentColor" strokeWidth="2.6" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                    </svg>
                    {audiobook.video_url ? "Video Ready" : "Download Video"}
                  </button>
                  {/* TEMP: PDF export test control */}
                  <button
                    onClick={handleDownloadPdf}
                    disabled={isDownloadingPdf}
                    className="inline-flex items-center gap-2 transition active:scale-[0.97]"
                    style={{
                      ...FREDOKA,
                      background: "#0065F8",
                      color: "#FFFFFF",
                      fontWeight: 700,
                      fontSize: 13,
                      border: "none",
                      borderRadius: 999,
                      padding: "10px 18px",
                      boxShadow: "0px 4px 0px #073B8E, 0px 10px 22px rgba(0,101,248,0.20)",
                      cursor: isDownloadingPdf ? "wait" : "pointer",
                      opacity: isDownloadingPdf ? 0.75 : 1,
                    }}
                    title="Download PDF"
                  >
                    <svg width="15" height="15" fill="none" stroke="currentColor" strokeWidth="2.6" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                    </svg>
                    {isDownloadingPdf ? "Downloading…" : "Download PDF"}
                  </button>
                </>
              )}

              <div ref={moreMenuRef} className="relative">
                <button
                  onClick={(e) => { e.stopPropagation(); setShowMoreMenu(!showMoreMenu); }}
                  aria-label="More actions"
                  className="inline-flex items-center justify-center transition active:scale-[0.97]"
                  style={{
                    ...FREDOKA,
                    background: "#FFFFFF",
                    color: "#0B1A33",
                    width: 42,
                    height: 42,
                    border: "2px solid rgba(11,26,51,0.08)",
                    borderRadius: 999,
                    boxShadow: "0px 2px 0px rgba(11,26,51,0.06)",
                    cursor: "pointer",
                  }}
                >
                  <svg width="18" height="18" fill="currentColor" viewBox="0 0 24 24">
                    <circle cx="5" cy="12" r="2" /><circle cx="12" cy="12" r="2" /><circle cx="19" cy="12" r="2" />
                  </svg>
                </button>

                {showMoreMenu && (
                  <div
                    className="absolute right-0 mt-2 z-50 overflow-hidden"
                    style={{
                      background: "#FFFFFF",
                      border: "2px solid rgba(11,26,51,0.08)",
                      borderRadius: 16,
                      boxShadow: "0 14px 40px rgba(35,49,78,0.16)",
                      minWidth: 230,
                      padding: "6px 0",
                    }}
                  >
                    {isOwner && (
                      <button
                        onClick={handleVisibilityToggle}
                        disabled={isUpdatingVisibility}
                        className="w-full flex items-center gap-3 px-4 py-2.5 text-left transition-colors hover:bg-blue-50"
                        style={{ ...FREDOKA, fontSize: 13, fontWeight: 700, color: "#0B1A33", opacity: isUpdatingVisibility ? 0.6 : 1 }}
                      >
                        <span style={{ width: 9, height: 9, borderRadius: "50%", background: audiobook.is_public ? "#10B981" : "#94A3B8" }} />
                        {audiobook.is_public ? "Public" : "Private"}
                        <span style={{ marginLeft: "auto", fontWeight: 500, color: "#5A6B85", fontSize: 12 }}>tap to switch</span>
                      </button>
                    )}
                    {audiobookId && isAuthenticated && (
                      <LikeButton contentType="storybook" contentId={audiobookId} variant="dropdown" />
                    )}
                    <button
                      onClick={() => { setShowMoreMenu(false); handleOpenShare(); }}
                      className="w-full flex items-center gap-3 px-4 py-2.5 text-left transition-colors hover:bg-blue-50"
                      style={{ ...FREDOKA, fontSize: 13, fontWeight: 700, color: "#0B1A33" }}
                    >
                      <svg width="15" height="15" fill="none" stroke="currentColor" strokeWidth="2.4" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
                      </svg>
                      Copy Link
                    </button>
                    {hasPages && !isProcessing && (
                      <button
                        onClick={(e) => { e.preventDefault(); e.stopPropagation(); setShowMoreMenu(false); setIsShareVideoModalOpen(true); }}
                        className="w-full flex items-center gap-3 px-4 py-2.5 text-left transition-colors hover:bg-blue-50"
                        style={{ ...FREDOKA, fontSize: 13, fontWeight: 700, color: "#0B1A33" }}
                      >
                        <svg width="15" height="15" fill="none" stroke="currentColor" strokeWidth="2.4" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.368 2.684 3 3 0 00-5.368-2.684z" />
                        </svg>
                        Share Video
                      </button>
                    )}

                    <div style={{ height: 1, background: "rgba(11,26,51,0.07)", margin: "6px 0" }} />
                    <div className="px-4 pb-1" style={{ ...FREDOKA, fontSize: 11, fontWeight: 700, letterSpacing: "0.12em", color: "#5A6B85", textTransform: "uppercase" }}>
                      Language {isTranslating && "(translating…)"}
                    </div>
                    <div className="max-h-52 overflow-y-auto">
                      {languageOptions.map((lang) => (
                        <button
                          key={lang.code}
                          onClick={async () => {
                            setShowMoreMenu(false);
                            setSelectedLanguage(lang.code);
                            await translateAudiobook(lang.code);
                          }}
                          disabled={isTranslating}
                          className="w-full flex items-center gap-3 px-4 py-2 text-left transition-colors hover:bg-blue-50"
                          style={{ ...FREDOKA, fontSize: 13, fontWeight: 600, color: getCurrentLanguage().code === lang.code ? "#0065F8" : "#0B1A33" }}
                        >
                          <span style={{ fontSize: 16 }}>{lang.flag}</span>
                          {lang.name}
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </>
          )}
        </div>

        <div className="hidden md:block">
          {/* Title — only while the book itself isn't on screen
              (processing/failed); the cover carries the title once readable */}
          {!isBookOnScreen && (
            <div className="text-center">
              <span
                style={{
                  ...FREDOKA,
                  color: "#0065F8",
                  fontSize: 12,
                  fontWeight: 700,
                  letterSpacing: "0.24em",
                  textTransform: "uppercase",
                }}
              >
                Storybook
              </span>
              <h1
                className="mt-1.5"
                style={{
                  ...FREDOKA,
                  color: "#2A3D6B",
                  fontWeight: 700,
                  fontSize: "clamp(1.9rem, 3.4vw, 2.7rem)",
                  letterSpacing: "-0.005em",
                  textTransform: "uppercase",
                  lineHeight: 1.08,
                  maxWidth: 880,
                  margin: "0 auto",
                  padding: "0 16px",
                }}
              >
                {audiobook.title || "Untitled Storybook"}
              </h1>
              {shouldShowCreator && (
                <p
                  style={{
                    ...FREDOKA,
                    color: "#5A6B85",
                    fontSize: 16,
                    margin: "12px 0 0",
                  }}
                >
                  by <strong style={{ color: "#0065F8" }}>{creatorName}</strong>
                </p>
              )}
            </div>
          )}
        </div>

        {/* Processing State */}
        {isProcessing && !isVoiceLive && <AudiobookProgressBar progress={progress} pageCount={audiobook?.page_count ?? audiobook?.total_pages} />}

        {/* Failed State */}
        {isFailed && (
          <div
            className="text-center mx-auto"
            style={{
              maxWidth: 520,
              background: "#FFFFFF",
              border: "2px solid rgba(220,38,38,0.22)",
              borderRadius: 22,
              padding: "28px 28px 32px",
              boxShadow: "0px 4px 0px rgba(11,26,51,0.06), 0px 12px 28px rgba(11,26,51,0.08)",
            }}
          >
            <h3 style={{ ...FREDOKA, color: "#B91C1C", fontWeight: 700, fontSize: 22, textTransform: "uppercase", letterSpacing: "-0.01em", margin: "0 0 8px" }}>
              Couldn't Generate Storybook
            </h3>
            <p style={{ ...FREDOKA, color: "#5A6B85", fontSize: 14, margin: 0 }}>
              Something went wrong while creating this one. Try starting a new storybook.
            </p>
          </div>
        )}

        {/* Ready State - Flip Book Layout (also shown live while an AI Voice
            Storybook is streaming pages in, before the backend marks it ready) */}
        {isBookOnScreen && (
          <div className={`w-full mx-auto ${isReadingSpread ? "md:pt-16 md:pb-2 md:px-0" : "md:py-4 md:px-2"}`}>
            {/* Audio Element */}
            <audio ref={audioRef} preload="metadata" />

            {waitingForNextPage && (
              <div
                className="text-center mx-auto mb-3"
                style={{
                  maxWidth: 420,
                  background: "rgba(0,101,248,0.08)",
                  border: "1.5px solid rgba(0,101,248,0.2)",
                  borderRadius: 16,
                  padding: "10px 16px",
                  ...FREDOKA,
                  color: "#073B8E",
                  fontSize: 14,
                  fontWeight: 600,
                }}
              >
                Generating next page… ({readyPageCount}/{totalPagesTarget || "?"})
              </div>
            )}

            {/* Flip Book Component (sky page theme) */}
            <div className="relative sb-theme-skybook">
              <AudiobookFlipBook
                pages={displayPages}
                currentPageIndex={currentPageIndex}
                onPageChange={handlePageChange}
                onLayoutModeChange={setIsSpreadLayout}
                isPlaying={isPlaying}
                renderPage={(page, pageNumber, totalPages, side) => (
                  <AudiobookSinglePage
                    page={page}
                    pageNumber={pageNumber}
                    totalPages={
                      // While generating, the loaded array is shorter than the
                      // real book — show "of 25", not "of 16".
                      Math.max(
                        Number(audiobook?.total_pages) || 0,
                        totalPages
                      ) || totalPages
                    }
                    side={side}
                    isPlaying={isPlaying}
                    onTogglePlayPause={togglePlayPause}
                    hideReadButton={isVoiceLive}
                    bookTitle={audiobook.title || ""}
                    animateStoryText={
                      isVoiceLive &&
                      // pageNumber -> displayPages index (numbering starts at
                      // the first story page; a cover at [0] shifts it by one)
                      !animatedTextPageIndexesRef.current.has(
                        displayPages[0]?.type === "cover" ? pageNumber : pageNumber - 1
                      )
                    }
                    storyTextAnimationSpeed={80}
                    onShareBook={handleOpenShare}
                    onReadAgain={() => setCurrentPageIndex(0)}
                    onCreateOwn={
                      isAuthenticated
                        ? () => navigate("/audiobooks/create")
                        : () => navigate("/auth?mode=signup&redirect=%2Faudiobooks%2Fcreate")
                    }
                    waitForNarration={isVoiceLive}
                    narrationActive={voiceSession.isAssistantSpeaking}
                    animateStoryImage={isVoiceLive}
                  />
                )}
              />

              {/* Compact floating mic control — the only "extra" UI an AI
                  Voice Storybook adds on top of the normal reader. Anchored
                  near the bottom-center of the book itself, never a separate
                  panel/layout. */}
              {isVoiceLive && (
                <div
                  className="absolute left-1/2 z-30"
                  style={{ bottom: 14, transform: "translateX(-50%)" }}
                >
                  <VoiceSessionPanel
                    status={voiceSession.status}
                    isMicOn={voiceSession.isMicOn}
                    isAssistantSpeaking={voiceSession.isAssistantSpeaking}
                    isCreatingPage={voiceSession.isCreatingPage}
                    onToggleMic={voiceSession.toggleMic}
                    errorMessage={voiceSession.errorMessage}
                    onStart={voiceSession.start}
                    onReconnect={voiceSession.reconnect}
                    onSendText={voiceSession.sendText}
                    onEndStory={voiceSession.requestEndStory}
                  />
                </div>
              )}
            </div>

            {/* Audio Control Toolbar - Visible when read-to-me is active */}
            {(isPlaying || isContinuousPlay) && (
              <div
                className={
                  isDesktopView
                    ? "mt-6 px-4 md:px-6 transition-all duration-300"
                    : "fixed bottom-0 left-0 right-0 z-50 px-3 py-3 md:px-6 md:py-4 transition-all duration-300"
                }
                style={{
                  background: isDesktopView ? "transparent" : "rgba(245,250,255,0.92)",
                  borderTop: isDesktopView ? "none" : "2px solid rgba(11,26,51,0.06)",
                  backdropFilter: isDesktopView ? "none" : "blur(14px)",
                  boxShadow: isDesktopView
                    ? "none"
                    : "0px -5px 0px rgba(11,26,51,0.04), 0px -10px 28px rgba(11,26,51,0.08)",
                }}
              >
                <div
                  className={`${isDesktopView ? "max-w-3xl" : "max-w-4xl"} mx-auto`}
                  style={
                    isDesktopView
                      ? {
                          background: "rgba(255,255,255,0.85)",
                          border: "2px solid rgba(11,26,51,0.08)",
                          borderRadius: 16,
                          padding: "10px 14px 12px",
                          boxShadow:
                            "0px 4px 0px rgba(11,26,51,0.06), 0px 12px 24px rgba(11,26,51,0.08)",
                        }
                      : undefined
                  }
                >
                  {/* Progress Bar */}
                  <div className="mb-2.5">
                    <div
                      onClick={handleSeek}
                      className="w-full cursor-pointer relative group"
                      style={{
                        height: 6,
                        background: "rgba(11,26,51,0.08)",
                        borderRadius: 999,
                      }}
                    >
                      <div
                        className="h-full rounded-full relative"
                        style={{
                          width: `${duration ? (currentTime / duration) * 100 : 0}%`,
                          background: "linear-gradient(90deg, #0065F8 0%, #00CAFF 100%)",
                          borderRadius: 999,
                          transition: "width 120ms linear",
                        }}
                      >
                        <div
                          className="absolute right-0 top-1/2 -translate-y-1/2 rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
                          style={{
                            width: 14,
                            height: 14,
                            background: "#FFFFFF",
                            border: "2px solid #0065F8",
                            boxShadow: "0px 2px 6px rgba(11,26,51,0.18)",
                          }}
                        />
                      </div>
                    </div>
                    <div className="flex justify-between items-center mt-1" style={{ ...FREDOKA, fontSize: 11, color: "#5A6B85", fontWeight: 600 }}>
                      <span>{formatTime(currentTime)}</span>
                      <span>{formatTime(duration)}</span>
                    </div>
                  </div>

                  {/* Controls */}
                  <div className="flex items-center justify-between gap-2 md:gap-4">
                    {/* Left: Stop button */}
                    <button
                      onClick={handleStop}
                      aria-label="Stop"
                      className="flex items-center justify-center transition active:scale-[0.95]"
                      style={{
                        width: 38,
                        height: 38,
                        background: "#FFFFFF",
                        color: "#5A6B85",
                        border: "2px solid rgba(11,26,51,0.10)",
                        borderRadius: "50%",
                        boxShadow: "0px 2px 0px rgba(11,26,51,0.06)",
                        cursor: "pointer",
                      }}
                    >
                      <svg width="14" height="14" fill="currentColor" viewBox="0 0 20 20">
                        <rect x="6" y="6" width="8" height="8" rx="1" />
                      </svg>
                    </button>

                    {/* Center: Play/Pause */}
                    <button
                      onClick={togglePlayPause}
                      disabled={!currentPage?.audio_url}
                      aria-label={isPlaying ? "Pause" : "Play"}
                      className="flex items-center justify-center transition active:scale-[0.96] disabled:opacity-50 disabled:cursor-not-allowed"
                      style={{
                        width: 54,
                        height: 54,
                        background: "#0065F8",
                        color: "#FFFFFF",
                        border: "none",
                        borderRadius: "50%",
                        boxShadow: "0px 4px 0px #073B8E, 0px 12px 26px rgba(0,101,248,0.30)",
                        cursor: currentPage?.audio_url ? "pointer" : "not-allowed",
                      }}
                    >
                      {isPlaying ? (
                        <svg width="22" height="22" fill="currentColor" viewBox="0 0 20 20">
                          <path d="M6 4h3v12H6V4zm5 0h3v12h-3V4z" />
                        </svg>
                      ) : (
                        <svg width="22" height="22" fill="currentColor" viewBox="0 0 20 20">
                          <path d="M6 4l10 6-10 6V4z" />
                        </svg>
                      )}
                    </button>

                    {/* Right: Speed + Volume */}
                    <div className="flex items-center gap-2 md:gap-2.5">
                      <div
                        className="flex items-center gap-1.5"
                        style={{
                          padding: "8px 10px",
                          background: "#FFFFFF",
                          border: "2px solid rgba(11,26,51,0.08)",
                          borderRadius: 999,
                          boxShadow: "0px 2px 0px rgba(11,26,51,0.05)",
                        }}
                      >
                        <svg width="13" height="13" fill="none" stroke="#0065F8" strokeWidth="2.4" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" d="M13 10V3L4 14h7v7l9-11h-7z" />
                        </svg>
                        <select
                          value={playbackRate}
                          onChange={(e) => setPlaybackRate(parseFloat(e.target.value))}
                          style={{
                            ...FREDOKA,
                            background: "transparent",
                            color: "#0B1A33",
                            fontSize: 12,
                            fontWeight: 700,
                            border: "none",
                            outline: "none",
                            cursor: "pointer",
                          }}
                        >
                          <option value="0.5">0.5x</option>
                          <option value="0.75">0.75x</option>
                          <option value="1">1x</option>
                          <option value="1.25">1.25x</option>
                          <option value="1.5">1.5x</option>
                          <option value="2">2x</option>
                        </select>
                      </div>

                      <div
                        className="hidden md:flex items-center gap-2"
                        style={{
                          padding: "8px 12px",
                          background: "#FFFFFF",
                          border: "2px solid rgba(11,26,51,0.08)",
                          borderRadius: 999,
                          boxShadow: "0px 2px 0px rgba(11,26,51,0.05)",
                        }}
                      >
                        <svg width="14" height="14" fill="#0065F8" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M9.383 3.076A1 1 0 0110 4v12a1 1 0 01-1.707.707L4.586 13H2a1 1 0 01-1-1V8a1 1 0 011-1h2.586l3.707-3.707a1 1 0 011.09-.217zM14.657 2.929a1 1 0 011.414 0A9.972 9.972 0 0119 10a9.972 9.972 0 01-2.929 7.071 1 1 0 01-1.414-1.414A7.971 7.971 0 0017 10c0-2.21-.894-4.208-2.343-5.657a1 1 0 010-1.414z" clipRule="evenodd" />
                        </svg>
                        <input
                          type="range"
                          min="0"
                          max="1"
                          step="0.1"
                          value={volume}
                          onChange={handleVolumeChange}
                          className="ab-vol"
                          style={{ width: 72 }}
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {/* No Pages Available */}
        {isReady && !hasPages && (
          <div
            className="text-center mx-auto"
            style={{
              maxWidth: 520,
              background: "#FFFFFF",
              border: "2px solid rgba(11,26,51,0.08)",
              borderRadius: 22,
              padding: "28px 28px 32px",
              boxShadow: "0px 4px 0px rgba(11,26,51,0.06), 0px 12px 28px rgba(11,26,51,0.08)",
            }}
          >
            <p style={{ ...FREDOKA, color: "#5A6B85", fontSize: 14, margin: 0 }}>
              No pages available for this storybook yet.
            </p>
          </div>
        )}

        {/* Recommended Storybooks — shown at bottom when viewing a storybook */}
        {isReady && audiobookId && (
          <div className="mt-8 pb-8">
            <RecommendedStorybooks currentStorybookId={audiobookId} />
          </div>
        )}
      </div>

      {/* Share Modal */}
      {isShareModalOpen && (
        <ShareModal
          isOpen={isShareModalOpen}
          onClose={() => setIsShareModalOpen(false)}
          shareUrl={withReferral(`${window.location.origin}/audiobooks/${audiobookId}${audiobook?.share_token ? `?token=${encodeURIComponent(audiobook.share_token)}` : ""}`, user?.id)}
          type="audiobook"
          storyId={audiobookId}
        />
      )}

      {/* AI Voice Storybook — full-screen "The End" celebration on finalize */}
      {showVoiceEnding && (() => {
        const endTitle = voiceEndingInfo?.title || audiobook?.title;
        const endCover =
          voiceEndingInfo?.thumbnail_url ||
          audiobook?.thumbnail_url ||
          audiobook?.cover_url ||
          audiobook?.cover_image_url;
        return (
          <div
            style={{
              position: "fixed", inset: 0, zIndex: 90,
              background: "linear-gradient(180deg, #F5FAFF 0%, #DCEBFF 100%)",
              display: "flex", flexDirection: "column",
              alignItems: "center", justifyContent: "center",
              overflow: "hidden", textAlign: "center", padding: "24px",
            }}
          >
            {[...Array(24)].map((_, i) => (
              <span
                key={i}
                style={{
                  position: "absolute", top: "-24px",
                  left: `${(i * 41) % 100}%`,
                  width: 9 + (i % 3) * 5, height: 9 + (i % 3) * 5,
                  borderRadius: i % 2 ? "50%" : "3px",
                  background: ["#0A6CFF", "#FFC83D", "#7C5CFF", "#34C759", "#FF6FA5"][i % 5],
                  animation: `voiceEndConfetti ${2.8 + (i % 5) * 0.5}s linear ${(i % 8) * 0.4}s infinite`,
                  zIndex: 0,
                }}
              />
            ))}
            <style>{`
              @keyframes voiceEndConfetti { to { transform: translateY(108vh) rotate(560deg); } }
              @keyframes voiceEndCoverIn { from { transform: scale(0.7); opacity: 0; } to { transform: scale(1); opacity: 1; } }
            `}</style>
            <div style={{ fontFamily: "'Fredoka', sans-serif", fontWeight: 700, fontSize: "clamp(36px, 6vw, 56px)", color: "#0B1A33", zIndex: 1 }}>
              The End
            </div>
            {endCover ? (
              <img
                src={endCover}
                alt={endTitle || "Your storybook"}
                style={{
                  width: "min(46vh, 78vw)", aspectRatio: "1 / 1", objectFit: "cover",
                  borderRadius: 24, marginTop: 22, zIndex: 1,
                  boxShadow: "0 24px 70px rgba(11,26,51,0.35)",
                  border: "6px solid #FFFFFF",
                  animation: "voiceEndCoverIn 0.6s ease-out",
                }}
              />
            ) : (
              <div
                style={{
                  width: "min(46vh, 78vw)", aspectRatio: "1 / 1", borderRadius: 24,
                  marginTop: 22, zIndex: 1, background: "#FFFFFF",
                  display: "flex", alignItems: "center", justifyContent: "center",
                  fontFamily: "'Fredoka', sans-serif", color: "#8595AD", fontSize: 16,
                  boxShadow: "0 24px 70px rgba(11,26,51,0.2)",
                }}
              >
                Painting your cover…
              </div>
            )}
            {endTitle && (
              <div style={{ fontFamily: "'Fredoka', sans-serif", fontWeight: 700, fontSize: "clamp(20px, 3.2vw, 28px)", color: "#0065F8", marginTop: 18, zIndex: 1 }}>
                {endTitle}
              </div>
            )}
            <div style={{ fontFamily: "'Fredoka', sans-serif", fontSize: 16, color: "#5A6B85", marginTop: 6, zIndex: 1 }}>
              You made a whole storybook. Amazing!
            </div>
            <button
              type="button"
              onClick={() => {
                setShowVoiceEnding(false);
                setCurrentPageIndex(0);
              }}
              style={{
                marginTop: 26, zIndex: 1,
                fontFamily: "'Fredoka', sans-serif", fontWeight: 700, fontSize: 18,
                color: "#FFFFFF", background: "#0065F8",
                border: "none", borderBottom: "4px solid #073B8E",
                borderRadius: 999, padding: "16px 44px", cursor: "pointer",
              }}
            >
              Read your book
            </button>
          </div>
        );
      })()}

      {/* AI Voice Storybook — continue past the free page limit */}
      <ConfirmationModal
        isOpen={showVoiceUpgradeDialog}
        onConfirm={handleContinueVoiceStorybook}
        onCancel={() => {
          if (isPurchasingContinue) return;
          // User is done extending — soft wrap-up. Backend also auto-finalizes
          // if they leave / disconnect without finishing, and always supplies a title.
          setShowVoiceUpgradeDialog(false);
          voiceSession.requestFinishStory?.();
        }}
        title="Continue your adventure?"
        starCost={25}
        currentStars={user?.pixley_stars ?? 0}
        actionType="continue_voice_storybook"
        confirmText={isPurchasingContinue ? "Continuing…" : "Continue Story"}
        cancelText="I'm done"
        isConfirmLoading={isPurchasingContinue}
        confirmDisabled={isPurchasingContinue}
      />

      {/* Share Video Modal */}
      <ShareVideoModal
        isOpen={isShareVideoModalOpen}
        onClose={() => {
          setIsShareVideoModalOpen(false);
        }}
        videoUrl={null}
        storyTitle={audiobook?.title}
        videoRef={null}
        storyId={audiobookId}
        user={user}
        subscriptionStatus={subscriptionStatus}
        apiClient={apiClient}
      />

      {/* Paywall Modal for non-premium / anonymous users attempting download */}
      <PaywallModal
        isOpen={isPaywallModalOpen}
        onClose={() => setIsPaywallModalOpen(false)}
        starCost={0}
        currentStars={0}
        contentType="download"
        apiClient={apiClient}
        user={user}
      />

      {/* Feedback Modal - Full version */}
      {user?.id && (
        <FeedbackModal
          isOpen={showFeedbackModal}
          onClose={() => {
            // Mark THIS audiobook as dismissed when user closes modal
            if (audiobookId) {
              setFeedbackDismissed(audiobookId, true);
              logger.log(`🔕 Feedback dismissed for audiobook ${audiobookId}`);
            }
            setShowFeedbackModal(false);
          }}
          variant="full"
          storyId={audiobookId}
          contentType="audio"
          sessionId={audiobookSessionId.current}
        />
      )}

      {/* Feedback Modal - Notification version */}
      {user?.id && (
        <FeedbackModal
          isOpen={showFeedbackNotification}
          onClose={() => {
            // Mark THIS audiobook as dismissed when user closes notification
            if (audiobookId) {
              setFeedbackDismissed(audiobookId, true);
              logger.log(`🔕 Feedback dismissed for audiobook ${audiobookId}`);
            }
            setShowFeedbackNotification(false);
          }}
          onOpenFullModal={() => {
            setShowFeedbackNotification(false);
            setShowFeedbackModal(true);
          }}
          variant="notification"
          storyId={audiobookId}
          contentType="audio"
          sessionId={audiobookSessionId.current}
        />
      )}

      <style>{`
        @keyframes ab-spin { to { transform: rotate(360deg); } }
        .ab-vol {
          appearance: none;
          height: 4px;
          background: rgba(11,26,51,0.10);
          border-radius: 999px;
          outline: none;
          cursor: pointer;
        }
        .ab-vol::-webkit-slider-thumb {
          appearance: none;
          width: 14px;
          height: 14px;
          border-radius: 50%;
          background: #0065F8;
          border: 2px solid #FFFFFF;
          box-shadow: 0px 2px 4px rgba(11,26,51,0.18);
        }
        .ab-vol::-moz-range-thumb {
          width: 14px;
          height: 14px;
          border-radius: 50%;
          background: #0065F8;
          border: 2px solid #FFFFFF;
          box-shadow: 0px 2px 4px rgba(11,26,51,0.18);
        }
      `}</style>
    </div>
  );
};

export default AudiobookPlayer;
